/**
 * 統一されたAPIクライアント
 * API_ENDPOINTSを使用して型安全なAPI呼び出しを提供
 */

import axios from '../lib/axios';
import { downloadCsv } from '../lib/downloadCsv';
import { API_ENDPOINTS } from '../types/api';
import type { ApiResponse, BottleAllocationResponse, LoginResponse, OrderFeedResponse, SaasFeaturesInfo, SaasPlanInfo } from '../types/api';
import type { CancelPreview } from '../lib/visitCancel';
import type { LastNightResponse } from '../lib/lastNightSummary';
import type { BackPolicy, BackPolicyItem, BackTransaction, Payroll } from '../types/models/back';
import type { Attendance, AttendanceBreak } from '../types/models/attendance';
import type { 
  EmployeeAssignment, 
  EmployeeAssignmentCreateRequest, 
  EmployeeAssignmentUpdateRequest,
  EmployeeAssignmentListResponse,
  EmployeeAssignmentDetailResponse,
  EmployeeAssignmentFilter
} from '../types/models/employeeAssignment';
import type { User } from '../types/models/user';
import type { RetirementCheck } from '../lib/retirementConfirm';
import type { ProfileResponse, ProfileUpdateRequest, ProfileUpdateResponse, PasswordFormData } from '../types/profile';
import type { SettingsResponse } from '../types/settings';
import type { CompanyInfo, OrganizationSettings } from '../types/models/company';
import type { OfficeDetail, OfficeCreateRequest, OfficeUpdateRequest } from '../types/models/office';
import type { Store, StoreCreateRequest, StoreUpdateRequest } from '../types/models/store';
import type { NotificationListResponse, UnreadCountResponse } from '../types/models/notification';
import type { 
  Employee, 
  EmployeeCreateRequest, 
  EmployeeUpdateRequest,
  EmployeeListResponse,
  EmployeeDetailResponse,
  EmployeeResponse,
  EmployeeSearchParams
} from '../types/models/employee';
import type { MenuItem, MenuItemCreateRequest, MenuItemUpdateRequest } from '../types/models/menuItem';
import type { Customer, CustomerCreateRequest, CustomerUpdateRequest } from '../types/models/customer';
import type { Payment, Receipt, SalesReport } from '../types/models/accounting';
import type { NotificationSetting, NotificationSettingUpdateRequest, NotificationMethod } from '../types/models/notificationSetting';
import type { ShiftType, ShiftTypeCreateRequest, ShiftTypeUpdateRequest } from '../types/models/shiftType';
import type { WeeklyShiftRequest, WeeklyShiftRequestCreateRequest, WeeklyShiftRequestUpdateRequest } from '../types/models/weeklyShiftRequest';
import type { ShiftTemplate, ShiftTemplateCreateRequest, ShiftTemplateUpdateRequest, ShiftTypeOption } from '../types/models/shiftTemplate';
import type { DeductionMaster, DeductionMasterCreateRequest, DeductionMasterUpdateRequest } from '../types/models/deductionMaster';
import type { OperationLog, OperationLogCreateRequest, OperationLogListResponse } from '../types/api';
import type { Rank } from '../types/models/rank';

// 操作ログ型をこのモジュールからも再エクスポートして、APIインデックスで利用できるようにする
export type { OperationLog, OperationLogCreateRequest, OperationLogListResponse };

/**
 * 認証関連API
 */
export const authAPI = {
  // ユーザー登録
  register: async (userData: any) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.auth.register, userData);
    return response.data;
  },

  // ログイン
  login: async (credentials: { email: string; password: string }) => {
    const response = await axios.post<LoginResponse>(API_ENDPOINTS.auth.login, credentials);
    console.log('API login response:', response);
    return response.data;
  },

  // ログアウト
  logout: async () => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.auth.logout);
    return response.data;
  },

  // 別の会社（所属しているお店）へ切り替える（2026-10-17）。応答はログインと同じ形（新しいトークン・今の会社・役割）。
  // `__companySwitch` の印で、通信の共通処理はこの要求の 401 / 409 を握らない（失敗は呼び出し元が理由を出す）
  switchCompany: async (companyId: number) => {
    const response = await axios.post<any>(API_ENDPOINTS.auth.switchCompany, { company_id: companyId }, { __companySwitch: true } as any);
    return response.data;
  },

  // メール認証再送
  resendVerification: async (email: string) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.auth.resendVerification, { email });
    return response.data;
  },

  // マスターコード取得
  getMasterCodes: async () => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.auth.masterCodes);
    return response.data;
  },

  // セットアップ
  setup: async (formData: any) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.auth.setup, formData);
    return response.data;
  },

  // ユーザーステータス確認
  checkStatus: async (params?: any) => {
    const response = await axios.get(API_ENDPOINTS.auth.checkStatus, { params });
    return response.data;
  },

  // パスワードリセット要求
  forgotPassword: async (email: string) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.auth.forgotPassword, { email });
    return response.data;
  },

  // パスワードリセット
  resetPassword: async (data: any) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.auth.resetPassword, data);
    return response.data;
  },

  // LINE OAuth state 生成（サーバーサイド HMAC 署名、payload に招待トークン等を埋め込み可）
  lineGenerateState: async (payload?: string) => {
    const params = payload ? { payload } : {};
    const response = await axios.get<{ state: string }>(API_ENDPOINTS.auth.line!.state, { params });
    return response.data;
  },

  // LINE 認証コールバック（invitation_token は state の payload に含まれる）
  lineCallback: async (code: string, state: string) => {
    const response = await axios.post<LoginResponse>(API_ENDPOINTS.auth.line!.callback, { code, state });
    return response.data;
  },

  // LINE アカウント連携
  lineLink: async (code: string, state: string, redirectUri: string) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.auth.line!.link, { code, state, redirect_uri: redirectUri });
    return response.data;
  },

  // LINE アカウント連携解除
  lineUnlink: async () => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.auth.line!.unlink);
    return response.data;
  },

  // LINE-only ユーザーにメール+パスワードを追加
  setupEmail: async (data: { email: string; password: string; password_confirmation: string }) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.auth.email!.setup, data);
    return response.data;
  },

  // 現在のユーザー情報取得
  getCurrentUser: async () => {
    const response = await axios.get<ApiResponse<User>>(API_ENDPOINTS.user);
    return response.data;
  },

  // プロフィール更新
  updateProfile: async (data: FormData) => {
    const response = await axios.post<ApiResponse<User>>(API_ENDPOINTS.profile.update, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

/**
 * ユーザー関連API
 */
export const userAPI = {
  // ユーザー情報取得
  getCurrentUser: async () => {
    const response = await axios.get<ApiResponse<User>>(API_ENDPOINTS.user);
    return response.data;
  },

  // ユーザー一覧取得
  getUsers: async (params?: { search?: string; per_page?: number }) => {
    const response = await axios.get<ApiResponse<User[]>>(API_ENDPOINTS.users, { params });
    return response.data;
  },
};

/**
 * 招待関連API
 */
export const invitationAPI = {
  create: async (payload: { employee_id: number; email: string; method?: 'email' | 'line' }) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.auth.invitations!.create, payload);
    return response.data;
  },
  accept: async (payload: { token: string; password: string }) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.auth.invitations!.accept, payload);
    return response.data;
  },
};

/**
 * プロフィール関連API
 */
export const profileAPI = {
  // プロフィール取得
  getProfile: async () => {
    const response = await axios.get<ProfileResponse>(API_ENDPOINTS.profile.show);
    return response.data;
  },

  // プロフィール更新
  updateProfile: async (data: ProfileUpdateRequest | FormData | PasswordFormData, config?: any) => {
    const response = await axios.post<ProfileUpdateResponse>(API_ENDPOINTS.profile.update, data as any, config);
    return response.data;
  },

  // パスワード変更専用
  updatePassword: async (data: PasswordFormData) => {
    const response = await axios.put<ProfileUpdateResponse>(API_ENDPOINTS.profile.updatePassword, data);
    return response.data;
  },

  // アバター削除
  deleteAvatar: async () => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.profile.deleteAvatar);
    return response.data;
  },
};

/**
 * 通知関連API
 */
export const notificationAPI = {
  // 通知一覧取得
  getNotifications: async (params?: { per_page?: number }) => {
    const response = await axios.get<NotificationListResponse>(API_ENDPOINTS.notifications.index, { params });
    return response.data; // NotificationListResponse型を直接返す
  },

  // 既読マーク（ID省略時は全件既読）
  markAsRead: async (notificationId?: string) => {
    const payload = notificationId ? { notification_id: notificationId } : {};
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.notifications.markAsRead, payload);
    return response.data;
  },

  // 通知削除
  deleteNotification: async (notificationId: string) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.notifications.destroy, {
      data: { notification_id: notificationId },
    });
    return response.data;
  },

  // 未読数取得
  getUnreadCount: async () => {
    const response = await axios.get<UnreadCountResponse>(API_ENDPOINTS.notifications.unreadCount);
    return response.data; // UnreadCountResponse型を直接返す
  },
};

/**
 * ダッシュボード関連API
 */
export const dashboardAPI = {
  // 統計情報取得
  getStats: async () => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.dashboard.stats);
    return response.data;
  },

  // 最近のアクティビティ取得
  getActivities: async () => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.dashboard.activities);
    return response.data;
  },

  // 休憩情報取得
  getBreaks: async () => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.dashboard.breaks);
    return response.data;
  },

  // 出勤
  clockIn: async (data?: { working_role?: 'staff' | 'cast' }) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.dashboard.clockIn, data);
    return response.data;
  },

  // 退勤
  clockOut: async () => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.dashboard.clockOut);
    return response.data;
  },

  // 休憩開始
  startBreak: async (data?: any) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.dashboard.breakStart, data);
    return response.data;
  },

  // 休憩終了
  endBreak: async () => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.dashboard.breakEnd);
    return response.data;
  },

  // 夜勤開始
  startNightShift: async () => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.dashboard.nightShiftStart);
    return response.data;
  },

  // 夜勤終了
  endNightShift: async () => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.dashboard.nightShiftEnd);
    return response.data;
  },

  // 夜勤情報取得
  getNightShiftInfo: async () => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.dashboard.nightShiftInfo);
    return response.data;
  },

  // 店舗の今日のキャスト（予定＋出勤状況）
  // includeAdmins: 管理者（配属なしで会社の全店舗の伝票に付けられる）も足す。オーダーの「誰の分」用（2026-10-03・O-2）
  getStoreTodayCasts: async (storeId: number, date?: string, opts?: { includeAdmins?: boolean }) => {
    const url = API_ENDPOINTS.dashboard.storeTodayCasts!(storeId);
    const params = (date || opts?.includeAdmins) ? { ...(date ? { date } : {}), ...(opts?.includeAdmins ? { include_admins: 1 } : {}) } : undefined;
    const response = await axios.get<{ success: boolean; data: Array<{ cast_id: number; display_name: string; employee_cd: string; plan?: { start_time?: string; end_time?: string; break_duration?: number }; attendance?: { status: string; clock_in?: string; clock_out?: string } }> }>(url, { params });
    return response.data;
  },
};

/**
 * バック管理API
 */
export const backAPI = {
  // キャスト自身の「今営業日(出勤中)」バック見込み (self 強制・パラメータ無し)。
  // 出勤中でなければ is_working=false / total 0。
  getCurrentPreview: async () => {
    const response = await axios.get<ApiResponse<{
      is_working: boolean;
      store_id: number | null;
      business_date: string;
      calculated_at: string;
      total_estimated_back: number;
      // バックとは別の手当（キャッチ手当など）。総バックには含まれない
      allowance_total?: number;
      rows: Array<{
        drink_name?: string;
        drink_category?: string | null;
        base_amount?: number | null;
        base_qty?: number | null;
        back_amount: number;
        back_type: string;
        business_date?: string | null;
        occurred_at?: string | null;
      }>;
      disclaimer_code: string;
    }>>('/api/cast/back-preview/current');
    return response.data;
  },

  // ポリシー一覧
  getPolicies: async (params?: { is_active?: boolean; page?: number }) => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.back.policies.index, { params });
    return response.data;
  },

  /**
   * 複数キャストについて「いま実際に当たっているポリシー」をまとめて引く（管理者限定）。
   *
   * 🔴 **FE 側で区画を判定しないこと**。以前は `CastManagement` が独自に
   * 「稼働タイプが一致するポリシー、無ければ all」で出していたが、ランクも店舗も
   * 有効期間も見ておらず、**管理者が見る一覧と実支給が食い違っていた**。
   * サーバ側は `BackPolicyPartitionResolver`（`index` と同じ 1 本）で判定している。
   */
  getApplicablePolicies: async (params: { store_id?: number | null; employee_ids: number[] }) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.back.policies.applicable, params);
    return response.data;
  },

  /**
   * 保存する前に、この内容で保存するとどのキャストに当たるか（要望③）。
   * 判定はサーバの `BackPolicyPartitionResolver::partitionWithDraft`。
   */
  previewPolicy: async (draft: {
    id?: number | null;
    employment_type: string;
    rank_id: number | null;
    store_id: number | null;
    valid_from: string;
    valid_to: string | null;
    is_active: boolean;
    has_items: boolean;
    /** 下書きの明細（保存と同じ形）。送るとバックの額の変化（`amount_changes`）も返る（2026-10-14） */
    items?: any[];
  }) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.back.policies.preview, draft);
    return response.data;
  },

  /**
   * このポリシーが**いま実際に当たっているキャスト**（ポリシー画面の逆引き）。
   * 判定はサーバの `BackPolicyPartitionResolver`（`applicable` と同じ 1 本）。
   * より具体的なポリシーに押しのけられているキャストは入らない。
   */
  getApplicableCasts: async (policyId: number) => {
    const response = await axios.get<ApiResponse<{
      policy_id: number;
      in_effect: boolean;
      not_in_effect_reason: 'inactive' | 'not_started' | 'expired' | 'no_items' | null;
      evaluated_store_ids: number[];
      /** 全店舗ポリシーで評価しなかった店舗の数（名前は返らない） */
      unevaluated_store_count: number;
      casts: Array<{ employee_id: number; name: string; store_id: number; store_name: string | null }>;
      undetermined: Array<{ store_id: number; store_name: string | null; count: number }>;
    }>>(API_ENDPOINTS.back.policies.applicableCasts(policyId));
    return response.data;
  },

  // ポリシー作成
  createPolicy: async (data: Partial<BackPolicy> & { items?: Partial<BackPolicyItem>[] }) => {
    const response = await axios.post<ApiResponse<BackPolicy>>(API_ENDPOINTS.back.policies.store, data);
    return response.data;
  },

  // ポリシー詳細
  getPolicy: async (id: number) => {
    const response = await axios.get<ApiResponse<BackPolicy>>(API_ENDPOINTS.back.policies.show(id));
    return response.data;
  },

  // ポリシー更新
  updatePolicy: async (id: number, data: Partial<BackPolicy>) => {
    const response = await axios.put<ApiResponse<BackPolicy>>(API_ENDPOINTS.back.policies.update(id), data);
    return response.data;
  },

  // ポリシー削除
  deletePolicy: async (id: number) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.back.policies.destroy(id));
    return response.data;
  },

  // ポリシーを今月末で終了（使われたポリシーは削除できないので、止めるときはこちら）
  endPolicyAtMonthEnd: async (id: number) => {
    const response = await axios.post<ApiResponse<{ id: number; valid_to: string }>>(
      API_ENDPOINTS.back.policies.endAtMonthEnd(id),
    );
    return response.data;
  },

  // ポリシー複製
  duplicatePolicy: async (id: number) => {
    const response = await axios.post<ApiResponse<BackPolicy>>(API_ENDPOINTS.back.policies.duplicate(id));
    return response.data;
  },

  // トランザクション一覧
  getTransactions: async (params?: { cast_id?: number; store_id?: number; from?: string; to?: string }) => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.back.transactions.index, { params });
    return response.data;
  },

  // 給与一覧
  getPayrolls: async (params?: { cast_id?: number; store_id?: number; month?: string; status?: 'draft'|'closed'|'paid' }) => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.back.payrolls.index, { params });
    return response.data;
  },

  // バック計算プレビュー
  calculate: async (payload: { from: string; to: string; cast_id?: number; store_id?: number }) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.back.calculate, payload);
    return response.data;
  },

  // バック計算確定
  commit: async (rows: Array<{ order_line_id: number; cast_id?: number; base_amount?: number; base_qty?: number; back_amount: number; occurred_at: string }>) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.back.commit, { rows });
    return response.data;
  },
};

/**
 * ランクAPI
 */
export const rankAPI = {
  getRanks: async () => {
    const response = await axios.get<ApiResponse<Rank[]>>(API_ENDPOINTS.ranks.index);
    return response.data;
  },

  createRank: async (data: { name: string; display_name: string; sort_order?: number; active?: boolean }) => {
    const response = await axios.post<ApiResponse<Rank>>(API_ENDPOINTS.ranks.store, data);
    return response.data;
  },

  updateRank: async (id: number, data: Partial<{ name: string; display_name: string; sort_order: number; active: boolean }>) => {
    const response = await axios.put<ApiResponse<Rank>>(API_ENDPOINTS.ranks.update(id), data);
    return response.data;
  },

  deleteRank: async (id: number) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.ranks.destroy(id));
    return response.data;
  },
};

/**
 * 従業員関連API
 */
export const employeeAPI = {
  // 従業員一覧取得
  getEmployees: async (params?: EmployeeSearchParams) => {
    const response = await axios.get<EmployeeListResponse>(API_ENDPOINTS.employees.index, { params });
    return response.data;
  },

  // 従業員詳細取得
  getEmployee: async (id: number) => {
    const response = await axios.get<EmployeeDetailResponse>(API_ENDPOINTS.employees.show(id));
    return response.data;
  },

  // 従業員作成
  createEmployee: async (data: EmployeeCreateRequest) => {
    const response = await axios.post<EmployeeResponse>(API_ENDPOINTS.employees.store, data);
    return response.data;
  },

  // 従業員更新
  updateEmployee: async (id: number, data: EmployeeUpdateRequest) => {
    const response = await axios.put<EmployeeResponse>(API_ENDPOINTS.employees.update(id), data);
    return response.data;
  },

  // 従業員削除
  deleteEmployee: async (id: number) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.employees.destroy(id));
    return response.data;
  },

  // 従業員復職（退職→在職）
  restoreEmployee: async (id: number) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.employees.restore(id));
    return response.data;
  },

  // 退職にする前の確認の材料（出勤中の店・未払いの日払い／給与・自分自身か）
  retirementCheck: async (id: number) => {
    const response = await axios.get<ApiResponse<RetirementCheck>>(API_ENDPOINTS.employees.retirementCheck(id));
    return response.data;
  },
};

// 時給履歴API
const wageHistoryUrl = (employeeId: number) => `${API_ENDPOINTS.employees.index}/${employeeId}/wage-histories`;

export const wageHistoryAPI = {
  getHistories: async (employeeId: number) => {
    const response = await axios.get<ApiResponse>(wageHistoryUrl(employeeId));
    return response.data;
  },
  createHistory: async (employeeId: number, data: { hourly_wage: number; effective_from: string; reason?: string }) => {
    const response = await axios.post<ApiResponse>(wageHistoryUrl(employeeId), data);
    return response.data;
  },
  updateHistory: async (employeeId: number, id: number, data: { hourly_wage?: number; effective_from?: string; reason?: string }) => {
    const response = await axios.put<ApiResponse>(`${wageHistoryUrl(employeeId)}/${id}`, data);
    return response.data;
  },
  deleteHistory: async (employeeId: number, id: number) => {
    const response = await axios.delete<ApiResponse>(`${wageHistoryUrl(employeeId)}/${id}`);
    return response.data;
  },
};

/**
 * 勤怠API
 */
export const attendanceAPI = {
  // 一覧
  getAttendances: async (params?: { ym?: string; from?: string; to?: string; store_id?: number }) => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.attendance.index, { params });
    return response.data;
  },

  // 出勤
  clockIn: async (data?: { working_role?: 'staff' | 'cast' }) => {
    const response = await axios.post<ApiResponse<Attendance>>(API_ENDPOINTS.attendance.clockIn, data);
    return response.data;
  },

  // 退勤
  clockOut: async () => {
    const response = await axios.post<ApiResponse<Attendance>>(API_ENDPOINTS.attendance.clockOut);
    return response.data;
  },

  // 休憩開始
  startBreak: async (payload?: { break_type?: 'lunch'|'coffee'|'other' }) => {
    const response = await axios.post<ApiResponse<AttendanceBreak>>(API_ENDPOINTS.attendance.breakStart, payload);
    return response.data;
  },

  // 休憩終了
  endBreak: async (breakId: number) => {
    const response = await axios.post<ApiResponse<AttendanceBreak>>(API_ENDPOINTS.attendance.breakEnd(breakId));
    return response.data;
  },

  // 統計取得
  getStatistics: async (params?: { from?: string; to?: string }) => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.attendance.statistics, { params });
    return response.data;
  },

  // 役割別統計（getStatisticsのdata内に role_hours を含むため同メソッドで取得可能だが、補助関数を用意）
  getRoleHours: async (params?: { from?: string; to?: string }) => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.attendance.statistics, { params });
    const data = response.data?.data as any;
    return data?.role_hours as { staff: number; cast: number } | undefined;
  },

  // 週次統計取得
  getWeeklyStats: async (params?: { from?: string; to?: string }) => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.attendance.weeklyStats, { params });
    return response.data;
  },

  // 月次統計取得
  getMonthlyStats: async (params?: { from?: string; to?: string }) => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.attendance.monthlyStats, { params });
    return response.data;
  },

  // 未退勤スタッフ一覧取得
  getOnDutyStaff: async (params: { store_id: number; business_date: string }) => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.attendance.onDuty, { params });
    return response.data;
  },

  // 強制退勤
  forceClockOut: async (attendanceId: number, data: { reason: string; clock_out_time?: string }) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.attendance.forceClockOut(attendanceId), data);
    return response.data;
  },

  // 強制退勤の「帰った時刻」の目安（その営業日に最後に接客していた時刻。2026-10-02・UX 監査 E-16）
  lastActivity: async (attendanceId: number) => {
    const response = await axios.get<{ success: boolean; data?: { last_activity_at: string | null; source: 'visit' | 'order' | null } }>(
      `/api/attendance/${attendanceId}/last-activity`,
    );
    return response.data;
  },

  // 打刻時刻の手動修正
  // - 認可: STAFF 以上 or 自分のリソース (OWN_RESOURCE)
  // - 制約: Payroll が closed/paid なら拒否
  // - reason は必須 (3-200 文字)
  update: async (
    attendanceId: number,
    // 理由は出勤・退勤で分けて送る（2026-09-29）。reason はまとめた理由（互換）
    data: { punch_clock_in?: string | null; punch_clock_out?: string | null; reason_clock_in?: string; reason_clock_out?: string; reason?: string },
  ) => {
    const response = await axios.patch<ApiResponse>(API_ENDPOINTS.attendance.update(attendanceId), data);
    return response.data;
  },
};

/**
 * 会社関連API
 */
export const companyAPI = {
  // 会社情報取得
  getInfo: async () => {
    const response = await axios.get<ApiResponse<CompanyInfo>>(API_ENDPOINTS.company.getInfo);
    console.log('API Response for getInfo:', response.data);
    return response.data.data; // CompanyInfo型を直接返す
  },

  // 会社情報更新
  updateInfo: async (formData: any) => {
    const response = await axios.put<ApiResponse<CompanyInfo>>(API_ENDPOINTS.company.updateInfo, formData);
    return response.data.data; // CompanyInfo型を直接返す
  },

  // 組織規模設定更新
  updateOrganizationSettings: async (settings: OrganizationSettings) => {
    console.log('Sending organization settings:', settings);
    const response = await axios.put<ApiResponse<CompanyInfo>>(API_ENDPOINTS.company.updateOrganizationSettings, settings);
    console.log('API Response for updateOrganizationSettings:', response.data);
    return response.data.data; // CompanyInfo型を直接返す
  },

  // 会社設定（提出ポリシーなど）取得
  getSettings: async () => {
    const response = await axios.get<ApiResponse<any>>(API_ENDPOINTS.company.getSettings);
    return response.data;
  },

  // 会社設定（提出ポリシーなど）更新
  updateSettings: async (data: any) => {
    const response = await axios.put<ApiResponse<any>>(API_ENDPOINTS.company.updateSettings, data);
    return response.data;
  },
};

/**
 * SaaS 課金プラン API (ADMIN 限定・Phase 1 は表示のみ)。
 * 来店会計の plan 系 (companyAPI.getSettings の plan_* 等) とは別物。
 */
export const saasAPI = {
  // 自社のプラン情報取得 (会社のオーナー以外は 403)
  getPlan: async () => {
    const response = await axios.get<ApiResponse<SaasPlanInfo>>(API_ENDPOINTS.saas.plan);
    return response.data;
  },
  // 機能制限の状態 (誰でも。ロックの有無と機能名だけ)
  getFeatures: async () => {
    const response = await axios.get<ApiResponse<SaasFeaturesInfo>>(API_ENDPOINTS.saas.features);
    return response.data;
  },
};

/**
 * 住所関連API
 */
export const addressAPI = {
  // 都道府県取得
  getPrefectures: async () => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.address.prefectures);
    return response.data;
  },

  // 郵便番号から住所取得
  getAddressByPostalCode: async (postalCode: string) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.address.postalCode, {
      postal_code: postalCode,
    });
    return response.data;
  },
};

/**
 * テスト用API
 */
export const testAPI = {
  // テストエンドポイント
  test: async () => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.test);
    return response.data;
  },
};

/**
 * 従業員配置管理API
 */
export const employeeAssignmentAPI = {
  // 一覧取得
  getAssignments: async (filters?: EmployeeAssignmentFilter) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const url = filters ? `${API_ENDPOINTS.employeeAssignments.index}?${params}` : API_ENDPOINTS.employeeAssignments.index;
    const response = await axios.get<ApiResponse<EmployeeAssignmentListResponse>>(url);
    return response.data;
  },

  // 作成
  createAssignment: async (data: EmployeeAssignmentCreateRequest) => {
    const response = await axios.post<ApiResponse<EmployeeAssignmentDetailResponse>>(
      API_ENDPOINTS.employeeAssignments.store, 
      data
    );
    return response.data;
  },

  // 詳細取得
  getAssignment: async (id: string | number) => {
    const response = await axios.get<ApiResponse<EmployeeAssignmentDetailResponse>>(
      API_ENDPOINTS.employeeAssignments.show(id)
    );
    return response.data;
  },

  // 更新
  updateAssignment: async (id: string | number, data: EmployeeAssignmentUpdateRequest) => {
    const response = await axios.put<ApiResponse<EmployeeAssignmentDetailResponse>>(
      API_ENDPOINTS.employeeAssignments.update(id), 
      data
    );
    return response.data;
  },

  // 削除
  deleteAssignment: async (id: string | number) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.employeeAssignments.destroy(id));
    return response.data;
  },

  // 従業員の現在の配置取得
  getCurrentAssignments: async (userId: string | number) => {
    const response = await axios.get<ApiResponse<EmployeeAssignment[]>>(
      API_ENDPOINTS.employeeAssignments.getCurrentAssignments(userId)
    );
    return response.data;
  },

  // 組織別配置取得
  getAssignmentsByOrganization: async (filters?: { office_id?: number; store_id?: number }) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const url = filters ? `${API_ENDPOINTS.employeeAssignments.getAssignmentsByOrganization}?${params}` : API_ENDPOINTS.employeeAssignments.getAssignmentsByOrganization;
    const response = await axios.get<ApiResponse<EmployeeAssignment[]>>(url);
    return response.data;
  },
};

/**
 * 残業申請管理API
 */
export const overtimeRequestAPI = {
  // 一覧取得
  getRequests: async () => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.overtimeRequests.index);
    return response.data;
  },

  // 作成
  createRequest: async (data: any) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.overtimeRequests.store, data);
    return response.data;
  },

  // 詳細取得
  getRequest: async (id: string | number) => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.overtimeRequests.show(id));
    return response.data;
  },

  // 更新
  updateRequest: async (id: string | number, data: any) => {
    const response = await axios.put<ApiResponse>(API_ENDPOINTS.overtimeRequests.update(id), data);
    return response.data;
  },

  // 削除
  deleteRequest: async (id: string | number) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.overtimeRequests.destroy(id));
    return response.data;
  },

  // 承認
  approveRequest: async (id: string | number) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.overtimeRequests.approve(id));
    return response.data;
  },

  // 却下
  rejectRequest: async (id: string | number) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.overtimeRequests.reject(id));
    return response.data;
  },

  // 従業員の申請履歴取得
  getEmployeeHistory: async (employeeId: string | number) => {
    const response = await axios.get<ApiResponse>(
      API_ENDPOINTS.overtimeRequests.getEmployeeHistory(employeeId)
    );
    return response.data;
  },

  // 承認待ち申請取得
  getPendingRequests: async () => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.overtimeRequests.getPendingRequests);
    return response.data;
  },

  // 統計情報取得
  getStatistics: async () => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.overtimeRequests.getStatistics);
    return response.data;
  },
};

/**
 * 設定関連API
 */
export const settingsAPI = {
  // 設定取得
  getSettings: async () => {
    const response = await axios.get<SettingsResponse>(API_ENDPOINTS.settings.index);
    return response.data.data; // SettingsData型を直接返す
  },

  // 設定更新
  updateSettings: async (settings: any) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.settings.update, settings);
    return response.data;
  },
};

/**
 * 事業所管理API
 */
export const officeAPI = {
  // 一覧取得
  getOffices: async () => {
    const response = await axios.get<ApiResponse<OfficeDetail[]>>(API_ENDPOINTS.offices.index);
    return response.data;
  },

  // 作成
  createOffice: async (data: OfficeCreateRequest) => {
    const response = await axios.post<ApiResponse<OfficeDetail>>(API_ENDPOINTS.offices.store, data);
    return response.data;
  },

  // 詳細取得
  getOffice: async (id: string | number) => {
    const response = await axios.get<ApiResponse<OfficeDetail>>(API_ENDPOINTS.offices.show(id));
    return response.data;
  },

  // 更新
  updateOffice: async (id: string | number, data: OfficeUpdateRequest) => {
    const response = await axios.put<ApiResponse<OfficeDetail>>(API_ENDPOINTS.offices.update(id), data);
    return response.data;
  },

  // 削除
  deleteOffice: async (id: string | number) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.offices.destroy(id));
    return response.data;
  },
};

/**
 * 店舗管理API
 */
export const storeAPI = {
  // 一覧取得
  // v8: デフォルトで assigned 店舗のみ返却。ADMIN 用の全店表示は include_unassigned: 1 を付ける
  // （管理画面・配属設定画面など全店リストが必要な場面で使用）
  getStores: async (params?: { office_id?: number; include_unassigned?: 0 | 1 }) => {
    const response = await axios.get<ApiResponse<Store[]>>(API_ENDPOINTS.stores.index, { params });
    return response.data;
  },
  // 店内フロアテーブル配置 取得
  getStoreTables: async (id: number) => {
    const url = API_ENDPOINTS.stores.tablesIndex!(id);
    const response = await axios.get<{ success: boolean; data: Array<{ table_key: string; label: string | null; type: 'normal' | 'vip' | 'counter'; capacity: number; x_pct: number; y_pct: number; sort_order: number; active: boolean }> }>(url);
    return response.data;
  },
  // キャスト本人の成績表（今月の指名・同伴・出勤・確定バックと、自分の過去。本人はサーバが認証から決める）
  getCastReportCard: async (id: number) => {
    const url = API_ENDPOINTS.stores.castReportCard!(id);
    const response = await axios.get<{ success: boolean; data: import('../lib/castReportCard').ReportCard }>(url);
    return response.data;
  },
  // テーブル稼働状況 取得（軽量）
  getStoreTableStatus: async (id: number) => {
    const url = API_ENDPOINTS.stores.tablesStatus!(id);
    const response = await axios.get<{
      success: boolean;
      data: Array<{
        table_key: string;
        status: 'vacant' | 'reserved' | 'occupied' | 'cleaning';
        party_size?: number | null;
        visit_status?: string | null;
        visit_id?: number | null;
        role?: 'main' | 'sub' | null;
        main_table_key?: string | null;
        customer_type?: 'free' | 'called' | null;
      }>;
    }>(url);
    return response.data;
  },
  // 店内フロアテーブル配置 保存（全件一括）
  saveStoreTables: async (id: number, tables: Array<{ table_key: string; label?: string | null; type: 'normal' | 'vip' | 'counter'; capacity: number; x_pct: number; y_pct: number; sort_order?: number; active?: boolean }>) => {
    const url = API_ENDPOINTS.stores.tablesSave!(id);
    const response = await axios.put<{ success: boolean }>(url, { tables });
    return response.data;
  },

  // 営業日（営業開始/終了/締め） 現在状態取得
  getBusinessDayCurrent: async (id: number) => {
    const url = API_ENDPOINTS.stores.businessDaysCurrent!(id);
    const response = await axios.get<{ success: boolean; data: { store_id: number; business_date: string; cutoff_time: string; status: 'preparing' | 'opened' | 'closed' | 'locked'; opened_at?: string | null; closed_at?: string | null; locked_at?: string | null; is_overdue?: boolean; calculated_business_date?: string } }>(url);
    return response.data;
  },
  // v1.2: 営業日一覧（管理画面一覧用途） - 店舗スコープは X-Store-ID を踏襲
  getStoreBusinessDays: async (params: {
    date_from: string;
    date_to: string;
    status?: 'all' | 'opened' | 'closed' | 'locked' | 'preparing';
  }) => {
    const { status, ...rest } = params;
    const query: any = { ...rest };
    if (status && status !== 'all') {
      query.status = status;
    }
    const response = await axios.get<{
      success: boolean;
      data: {
        business_days: Array<{
          id: number;
          business_date: string;
          status: 'preparing' | 'opened' | 'closed' | 'locked';
          opened_at?: string;
          closed_at?: string;
          locked_at?: string;
          reopened_at?: string;
          reopened_by_employee_id?: number;
          reopen_reason?: string;
        }>;
      };
      message?: string;
      error_code?: string;
    }>('/api/store-business-days', { params: query });
    return response.data;
  },
  openBusinessDay: async (id: number, businessDate: string) => {
    const url = API_ENDPOINTS.stores.businessDaysOpen!(id, businessDate);
    const response = await axios.post<{ success: boolean; data?: any; warnings?: Array<{ code: string; message: string; meta?: any }>; message?: string; error_code?: string; meta?: any }>(url);
    return response.data;
  },
  closeBusinessDay: async (id: number, businessDate: string, options?: { force?: boolean }) => {
    const url = API_ENDPOINTS.stores.businessDaysClose!(id, businessDate);
    const payload = options?.force ? { force: true } : {};
    const response = await axios.post<{ success: boolean; data?: any; message?: string; error_code?: string; meta?: any; warnings?: any[] }>(url, payload);
    return response.data;
  },
  lockBusinessDay: async (id: number, businessDate: string) => {
    const url = API_ENDPOINTS.stores.businessDaysLock!(id, businessDate);
    const response = await axios.post<{ success: boolean; data?: any; message?: string; error_code?: string; meta?: any }>(url);
    return response.data;
  },
  // 締め解除（locked → closed）
  reopenLock: async (storeId: number, businessDayId: number, reason: string) => {
    const url = API_ENDPOINTS.stores.businessDaysReopenLock!(storeId, businessDayId);
    const response = await axios.post<{
      success: boolean;
      data?: {
        id: number;
        status: 'closed';
        reopened_at: string;
        reopened_by: number;
        reopen_reason: string;
        [key: string]: any;
      };
      error_code?: string;
      message?: string;
    }>(url, { reason });
    return response.data;
  },
  // 締め候補サマリ（未精算/過去未精算/未退勤の件数）
  getPendingSummary: async (id: number, businessDate: string) => {
    const url = API_ENDPOINTS.stores.businessDaysPendingSummary!(id, businessDate);
    const response = await axios.get<{
      success: boolean;
      data: {
        business_day_id?: number;
        business_date: string;
        status: 'preparing' | 'opened' | 'closed' | 'locked';
        pending: {
          unclosed_visits: number;
          past_unclosed_visits: number;
          on_duty_people: number;
        };
      };
    }>(url);
    return response.data;
  },
  // 締めた直後の「今夜のまとめ」（売上・日払い・現金。黒服・管理者だけ）
  getBusinessDaySummary: async (id: number, businessDate: string) => {
    const url = API_ENDPOINTS.stores.businessDaysSummary!(id, businessDate);
    const response = await axios.get<{
      success: boolean;
      data: {
        business_date: string;
        status: 'preparing' | 'opened' | 'closed' | 'locked';
        sales: { total: number; count: number; by_method: Array<{ method: string; amount: number; count: number }> };
        daily_payroll: { total: number; count: number; by_method: Array<{ method: string; amount: number; count: number }> };
        cash: { sales: number; daily_payroll: number; expected: number };
        correction_count: number;
      };
    }>(url);
    return response.data;
  },
  // 店舗設定取得（AbortSignal 対応: 店舗切替時の stale response 破棄用）
  getStoreSettings: async (id: number, options?: { signal?: AbortSignal }) => {
    const response = await axios.get<ApiResponse<any>>(
      API_ENDPOINTS.stores.show(id) + '/settings',
      options?.signal ? { signal: options.signal } : undefined,
    );
    return response.data;
  },
  updateStoreSettings: async (id: number, data: any) => {
    const response = await axios.put<ApiResponse<any>>(API_ENDPOINTS.stores.show(id) + '/settings', data);
    return response.data;
  },
  // 任意控除（店舗上書き）
  getStoreDeductions: async (id: number) => {
    const url = API_ENDPOINTS.stores.deductionsIndex!(id);
    const response = await axios.get<{ success: boolean; data: Array<{ deduction_id: number; deduction_cd: string | null; name: string; unit: string; calc_type: string; company_default_amount: number; pre_tax: boolean; company_active: boolean; override_amount: number | null; store_active: boolean | null }> }>(url);
    return response.data;
  },
  upsertStoreDeduction: async (id: number, deductionId: number, payload: { override_amount?: number | null; active?: boolean }) => {
    const url = API_ENDPOINTS.stores.deductionsUpdate!(id, deductionId);
    const response = await axios.put<{ success: boolean }>(url, payload);
    return response.data;
  },
  deleteStoreDeduction: async (id: number, deductionId: number) => {
    const url = API_ENDPOINTS.stores.deductionsDelete!(id, deductionId);
    const response = await axios.delete<{ success: boolean }>(url);
    return response.data;
  },

  // 作成
  createStore: async (data: StoreCreateRequest) => {
    const response = await axios.post<ApiResponse<Store>>(API_ENDPOINTS.stores.store, data);
    return response.data;
  },

  // 詳細取得
  getStore: async (id: string | number) => {
    const response = await axios.get<ApiResponse<Store>>(API_ENDPOINTS.stores.show(id));
    return response.data;
  },

  // 更新
  updateStore: async (id: string | number, data: StoreUpdateRequest) => {
    const response = await axios.put<ApiResponse<Store>>(API_ENDPOINTS.stores.update(id), data);
    return response.data;
  },

  // 削除
  deleteStore: async (id: string | number) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.stores.destroy(id));
    return response.data;
  },

  // レシート直接印刷（サーバー経由 TCP:9100）
  printReceipt: async (storeId: number, data: {
    printer_ip: string;
    receipt: Record<string, any>;
    is_store_copy?: boolean;
    paper_width?: number;
  }) => {
    const response = await axios.post<ApiResponse>(
      API_ENDPOINTS.stores.show(storeId) + '/print/receipt',
      data,
    );
    return response.data;
  },

  // CloudPRNT 印刷ジョブ登録
  submitCloudPrintJob: async (storeId: number, data: {
    receipt: Record<string, any>;
    print_twice?: boolean;
    paper_width?: number;
  }) => {
    const response = await axios.post<ApiResponse & { group_id?: string; job_count?: number }>(
      API_ENDPOINTS.stores.show(storeId) + '/print-jobs',
      data,
    );
    return response.data;
  },

  // 管理者ダッシュボード統計（キャスト数 / 来店数 / 売上 / バック支払額 / 担当未設定の呼び客件数）
  getAdminStats: async (storeId: number) => {
    const response = await axios.get<{
      success: boolean;
      data: {
        active_casts: number;
        today_visits: number;
        yesterday_visits: number;
        visits_diff: number;
        today_sales: number;
        yesterday_sales: number;
        sales_diff: number;
        today_back: number;
        yesterday_back: number;
        back_diff: number;
        /** 担当未設定の呼び客（営業中・SEATED/SERVING/CHECKOUT のみ）。バック確定前の警告指標 */
        unassigned_called_visits_today: number;
      };
    }>(API_ENDPOINTS.stores.show(storeId) + '/dashboard/admin-stats');
    return response.data;
  },

  // オーナーの「昨夜のまとめ」（売上は売上レポートと同じ数え方・先週の同じ曜日と比べる。2026-11-03）
  getLastNight: async (storeId: number, businessDate?: string) => {
    const response = await axios.get<{ success: boolean; data: LastNightResponse }>(
      API_ENDPOINTS.stores.show(storeId) + '/dashboard/last-night',
      { params: businessDate ? { business_date: businessDate } : undefined },
    );
    return response.data;
  },

  // 店舗サマリ統計（アクティブテーブル / 今日のオーダー / ボトル出庫）
  getStoreStats: async (storeId: number) => {
    const response = await axios.get<{
      success: boolean;
      data: {
        active_tables: number;
        today_orders: number;
        yesterday_orders: number;
        orders_diff: number;
        bottle_count: number;
        yesterday_bottles: number;
        bottle_diff: number;
      };
    }>(API_ENDPOINTS.stores.show(storeId) + '/dashboard/store-stats');
    return response.data;
  },
};


/**
 * セッション内キャッシュ（モジュールスコープ）
 * - 同一キーの同時リクエストはPromiseを共有
 * - TTL内は再取得しない
 */
const __cache = {
  menuItems: new Map<string, { fetchedAt: number; promise: Promise<ApiResponse> }>(),
};

/**
 * メニューAPI
 */
export const menuAPI = {
  // メニューアイテム一覧取得
  getMenuItems: async (params?: { category?: string; search?: string }) => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.menu.items, { params });
    return response.data;
  },

  /**
   * メニューアイテム一覧取得（キャッシュ付き）
   * @param params クエリ
   * @param options ttlMs: キャッシュTTL（デフォルト5分）, force: 強制再取得, cacheKey: キャッシュキー上書き
   */
  getMenuItemsCached: async (
    params?: { category?: string; search?: string },
    options?: { ttlMs?: number; force?: boolean; cacheKey?: string }
  ) => {
    const ttlMs = options?.ttlMs ?? 5 * 60 * 1000;
    const cacheKey = options?.cacheKey ?? `menu.items:${JSON.stringify(params ?? {})}`;
    const now = Date.now();

    if (!options?.force) {
      const cached = __cache.menuItems.get(cacheKey);
      if (cached && now - cached.fetchedAt < ttlMs) {
        return await cached.promise;
      }
    }

    const promise = (async () => {
      try {
        const response = await axios.get<ApiResponse>(API_ENDPOINTS.menu.items, { params });
        return response.data;
      } catch (e) {
        // 失敗したキャッシュは残さない
        __cache.menuItems.delete(cacheKey);
        throw e;
      }
    })();

    __cache.menuItems.set(cacheKey, { fetchedAt: now, promise });
    return await promise;
  },

  // メニューアイテム詳細取得
  getMenuItem: async (id: number) => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.menu.items + `/${id}`);
    return response.data;
  },

  // メニュー一覧キャッシュをクリア（管理画面更新後などで利用）
  clearMenuItemsCache: (prefix: string = 'menu.items:') => {
    __cache.menuItems.forEach((_v, key) => {
      if (key.startsWith(prefix)) __cache.menuItems.delete(key);
    });
  },
};

/**
 * オーダーAPI
 */
export const orderAPI = {
  // オーダー明細一覧取得
  getOrders: async (params?: { visit_id?: number; cast_id?: number }) => {
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.orders.index, { params });
    return response.data;
  },

  // 新着オーダー（afterId 無しは基準点の cursor だけ返る。顧客要望台帳 #24）
  getFeed: async (storeId: number, afterId?: number | null) => {
    const response = await axios.get<ApiResponse<OrderFeedResponse>>(API_ENDPOINTS.orders.feed(storeId), {
      params: afterId == null ? {} : { after_id: afterId },
    });
    return response.data;
  },

  // オーダー明細作成
  createOrder: async (data: {
    visit_id: number;
    menu_item_id: number;
    cast_id?: number;
    staff_id?: number;
    qty: number;
    note?: string;
    // 2026-06-18 Phase 5B (memory: project_female_set_pricing_design):
    // 延長サブモーダルで同一延長操作の複数 line に同じ UUID を持たせる (= サーバ側で集約判定)。
    // 「男性延長 + 女性延長」 の 2 line 同時投入で plan_ends_at が +60 分 × 2 = +120 分にならない保険。
    extension_op_id?: string;
    // 男女別単価編集モードで line ごとの単価を上書き (= 例: 女性分 ¥2,500)。
    unit_price_override?: number;
    // 多端末の二重延長ガード (2026-07-11): 延長メニュー注文時、モーダルが表示していた時点の
    // plan_duration_minutes。サーバ側の現在値と不一致なら 409 (別端末で延長済み)。
    expected_plan_duration_minutes?: number;
    // 延長・延長の取り消しの世代番号（2026-10-28）。モーダルが表示していた時点の値。
    // サーバは分数と両方とも比べる（取り消しで分数が元に戻っても、古いモーダルの確定を 409 にする）
    expected_plan_extension_revision?: number;
    // 2026-09-19 (顧客要望台帳 #5): セット時間が終了した飲み放題伝票で、無料ドリンクを
    // そのまま ¥0 で出すことを明示的に選んだ場合のみ true。既定では 409 PLAN_EXPIRED で止まる。
    allow_after_plan_end?: boolean;
    // 二重受付の拒否（2026-10-05・UX 監査 O-1）: 品ごとの UUID。同じ伝票で同じ番号がもう一度来たら、サーバは何も作らず最初の結果を返す
    // （電波が切れて「送れたか分からない」ときの押し直しで、同じ品が二重に入り、キャストのバックも倍になるのを防ぐ）。省略可
    client_request_id?: string;
  }) => {
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.orders.store, data);
    return response.data;
  },

  // オーダー明細更新
  // `allow_after_plan_end`: セット時間が終わった飲み放題の伝票で、無料ドリンクの数を増やすことを明示的に選んだとき true。
  // 既定では 409 PLAN_EXPIRED で止まる（顧客要望台帳 #5 の残り a・2026-11-01。新しい注文の `createOrder` と同じ）
  updateOrder: async (id: number, data: { qty: number; note?: string; allow_after_plan_end?: boolean }) => {
    const response = await axios.put<ApiResponse>(API_ENDPOINTS.orders.update(id), data);
    return response.data;
  },

  // オーダー明細削除
  // revertExtensionTime: 延長の明細を「取り消す」ときだけ true（2026-10-28・酔客UX監査 R-3）。
  //   サーバはこの印があるときだけ、その延長で足したセット時間を戻す。印の無い削除（「1 つ減らす」「編集」が裏で消す・
  //   営業中に開いたままの古い画面）は今までどおり時間を戻さない（「編集」で 2 回分をまとめると 1 回分の時間が黙って戻るのを防ぐ）
  deleteOrder: async (id: number, opts?: { revertExtensionTime?: boolean }) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.orders.destroy(id), {
      params: opts?.revertExtensionTime ? { revert_extension_time: 1 } : undefined,
    });
    return response.data;
  },

  // ボトル/シャンパンバックの配分取得 (memory: project_back_allocations_phase2)
  getAllocations: async (orderLineId: number) => {
    const response = await axios.get<BottleAllocationResponse>(API_ENDPOINTS.orders.allocations(orderLineId));
    return response.data;
  },

  // ボトル/シャンパンバックの配分を総入替で保存
  saveAllocations: async (
    orderLineId: number,
    allocations: Array<{ cast_id: number; allocated_amount: number; allocation_method?: string }>,
  ) => {
    const response = await axios.put<BottleAllocationResponse>(
      API_ENDPOINTS.orders.allocations(orderLineId),
      { allocations },
    );
    return response.data;
  },
};

/**
 * メニューアイテムAPI
 */
export const menuItemAPI = {
  // メニューアイテム一覧取得
  getMenuItems: async (params?: { category?: string; is_active?: boolean; store_id?: number }) => {
    const response = await axios.get<ApiResponse<MenuItem[]>>(API_ENDPOINTS.menuItems.index, { params });
    return response.data;
  },

  // メニューアイテム詳細取得
  getMenuItem: async (id: number) => {
    const response = await axios.get<ApiResponse<MenuItem>>(API_ENDPOINTS.menuItems.show(id));
    return response.data;
  },

  // メニューアイテム作成
  createMenuItem: async (data: MenuItemCreateRequest) => {
    const response = await axios.post<ApiResponse<MenuItem>>(API_ENDPOINTS.menuItems.store, data);
    return response.data;
  },

  // メニューアイテム更新
  updateMenuItem: async (id: number, data: MenuItemUpdateRequest) => {
    const response = await axios.put<ApiResponse<MenuItem>>(API_ENDPOINTS.menuItems.update(id), data);
    return response.data;
  },

  // メニューアイテム削除
  deleteMenuItem: async (id: number) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.menuItems.destroy(id));
    return response.data;
  },

  // メニュー画像アップロード (multipart)。返却は解決済みの image_url / image_thumb_url / image_source。
  uploadMenuItemImage: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await axios.post<ApiResponse<{ image_url: string | null; image_thumb_url: string | null; image_source: string }>>(
      API_ENDPOINTS.menuItems.image(id),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  // メニュー画像削除
  deleteMenuItemImage: async (id: number) => {
    const response = await axios.delete<ApiResponse<{ image_url: string | null; image_thumb_url: string | null; image_source: string }>>(
      API_ENDPOINTS.menuItems.image(id)
    );
    return response.data;
  },

  // 店舗別オーバーライド更新（単価/有効フラグ）
  upsertStoreOverride: async (
    id: number,
    payload: { store_id: number; unit_price_override?: number | null; active_override?: boolean | null }
  ) => {
    // 新ルート: stores/{storeId}/menu-items/{id}/store-overrides
    const response = await axios.put<ApiResponse>(
      API_ENDPOINTS.menuItems.storeOverrides(payload.store_id, id),
      {
        unit_price_override: payload.unit_price_override,
        active_override: payload.active_override,
      }
    );
    return response.data;
  },

  // 店舗別オーバーライド削除（会社共通に戻す）
  deleteStoreOverride: async (id: number, store_id: number) => {
    // 新ルート: stores/{storeId}/menu-items/{id}/store-overrides
    const response = await axios.delete<ApiResponse>(
      API_ENDPOINTS.menuItems.storeOverrides(store_id, id)
    );
    return response.data;
  },
};

/**
 * 顧客管理API
 */
export const customerAPI = {
  // 顧客一覧取得
  getCustomers: async (params?: { search?: string }) => {
    const response = await axios.get<ApiResponse<Customer[]>>(API_ENDPOINTS.customers.index, { params });
    return response.data;
  },

  // 顧客詳細取得
  getCustomer: async (id: number) => {
    const response = await axios.get<ApiResponse<Customer>>(API_ENDPOINTS.customers.show(id));
    return response.data;
  },

  // 顧客登録
  createCustomer: async (data: CustomerCreateRequest) => {
    const response = await axios.post<ApiResponse<Customer>>(API_ENDPOINTS.customers.store, data);
    return response.data;
  },

  // 顧客情報更新
  updateCustomer: async (id: number, data: CustomerUpdateRequest) => {
    const response = await axios.put<ApiResponse<Customer>>(API_ENDPOINTS.customers.update(id), data);
    return response.data;
  },

  // 顧客削除
  deleteCustomer: async (id: number) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.customers.destroy(id));
    return response.data;
  },
};

/**
 * 来店受付API
 */
export const visitAPI = {
  // アクティブ伝票一覧（フッター「伝票」用 / CAST はサーバ側で関連伝票のみにフィルタ）
  getActiveVisits: async (storeId: number, options?: { signal?: AbortSignal }) => {
    const url = API_ENDPOINTS.stores.visitsActive!(storeId);
    const response = await axios.get<ApiResponse>(url, { signal: options?.signal });
    return response.data;
  },

  // 来店記録一覧取得
  getVisits: async (params?: { store_id?: number; status?: string | string[]; table_no?: string; business_date?: string; per_page?: number }) => {
    const storeId = params?.store_id;
    if (storeId) {
      const url = API_ENDPOINTS.stores.visitsIndex!(storeId);
      const { store_id: _omit, ...rest } = params || {};
      const response = await axios.get<ApiResponse>(url, { params: rest });
      return response.data;
    }
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.visits.index, { params });
    return response.data;
  },

  // 来店記録詳細取得
  getVisit: async (id: number, storeId?: number) => {
    if (storeId) {
      const url = API_ENDPOINTS.stores.visitsShow!(storeId, id);
      const response = await axios.get<ApiResponse>(url);
      return response.data;
    }
    // 互換（旧ルート）。Laravel側で 410 の可能性があるため、基本は storeId を渡すこと。
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.visits.show(id));
    return response.data;
  },

  // 来店受付
  createVisit: async (data: {
    table_no: string;
    party_size: number;
    // 2026-06-17 Phase 4 + 2026-06-18 Phase 5A (memory: project_female_set_pricing_design):
    // 受付時の来店構成 snapshot。 'general'=通常 / 'female_only'=全員女性 / 'mixed_with_female'=混在で女性あり。
    // mixed_with_female は料金切替条件ではなく、 集計 + 延長サブモーダルのサジェスト発火条件として使う。
    party_composition?: 'general' | 'female_only' | 'mixed_with_female';
    plan_type: 'time' | 'bottle' | 'all_you_can_drink';
    plan_duration_minutes?: number;
    arrival_at?: string;
    set_pricing_rule_id?: number;
    plan_premium_drinks?: boolean;
    customer_type?: 'free' | 'called';
    primary_cast_id?: number;
    is_vip?: boolean;
    estimated_bill?: number;
    store_id?: number;
    customer_id?: number;
    customer_name?: string;
    customer_phone?: string;
    customer_rank?: 'regular' | 'vip' | 'black';
    customer_email?: string;
    customer_note?: string;
    notes?: string;
  }) => {
    const storeId = data.store_id;
    if (storeId) {
      const response = await axios.post<ApiResponse>(API_ENDPOINTS.stores.visitsIndex(storeId), data);
      return response.data;
    }
    // 互換（旧ルート）。Laravel側で 410 の可能性があるため、基本は store_id を渡すこと。
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.visits.store, data);
    return response.data;
  },

  // 来店記録更新
  updateVisit: async (id: number, storeId: number | undefined, data: {
    table_no?: string;
    party_size?: number;
    customer_type?: 'free' | 'called';
    primary_cast_id?: number | null;
    note?: string;
    status?: string;
    customer_id?: number | null;
  }) => {
    if (storeId && API_ENDPOINTS.stores.visitsShow) {
      const url = API_ENDPOINTS.stores.visitsShow(storeId, id);
      const response = await axios.put<ApiResponse>(url, data);
      return response.data;
    }
    const response = await axios.put<ApiResponse>(API_ENDPOINTS.visits.update(id), data);
    return response.data;
  },

  // 来店記録削除
  deleteVisit: async (id: number, storeId?: number) => {
    if (storeId) {
      const url = API_ENDPOINTS.stores.visitsShow!(storeId, id);
      const response = await axios.delete<ApiResponse>(url);
      return response.data;
    }
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.visits.destroy(id));
    return response.data;
  },

  // 提供開始（手動）: SEATED -> SERVING
  // 2026-06-20 (memory: project_visit_time_fields_consistency):
  //   serviceStartAt を渡すと、 backend で service_start_at = 指定時刻 として確定 + plan_ends_at を再計算。
  //   未指定なら現在時刻が採用される (= 後方互換)。
  startServing: async (visitId: number, storeId: number, options?: { serviceStartAt?: string; arrivalAt?: string }) => {
    const url = API_ENDPOINTS.stores.visitsStartServing!(storeId, visitId);
    // service_start_at / arrival_at はそれぞれ指定があるときのみ送る。
    // arrival_at は提供開始モーダルで受付時刻を編集したときだけ送られ、 未編集なら既存値を温存する。
    const body: { service_start_at?: string; arrival_at?: string } = {};
    if (options?.serviceStartAt) body.service_start_at = options.serviceStartAt;
    if (options?.arrivalAt) body.arrival_at = options.arrivalAt;
    const response = await axios.post<ApiResponse>(url, body);
    return response.data;
  },

  // 会計開始（手動）: SERVING -> CHECKOUT
  startCheckout: async (visitId: number, storeId: number) => {
    const url = API_ENDPOINTS.stores.visitsStartCheckout!(storeId, visitId);
    const response = await axios.post<ApiResponse>(url);
    return response.data;
  },

  // 提供中に戻す（手動）: CHECKOUT -> SERVING
  backToServing: async (visitId: number, storeId: number) => {
    const url = API_ENDPOINTS.stores.visitsBackToServing!(storeId, visitId);
    const response = await axios.post<ApiResponse>(url);
    return response.data;
  },

  // 伝票の取消の確認（明細・バックの見込み・払い済みか）。純粋スタッフ限定（2026-10-30）
  getVisitCancelPreview: async (visitId: number, storeId: number) => {
    const url = API_ENDPOINTS.stores.visitsCancelPreview!(storeId, visitId);
    const response = await axios.get<ApiResponse<CancelPreview>>(url);
    return response.data;
  },

  // 伝票の取消（請求 0 円で閉じる）。cancel_back_order_line_ids はバックも取り消す明細（空 = 全部残す）
  cancelVisit: async (visitId: number, storeId: number, body: { reason: string; cancel_back_order_line_ids: number[] }) => {
    const url = API_ENDPOINTS.stores.visitsCancel!(storeId, visitId);
    const response = await axios.post<ApiResponse>(url, body);
    return response.data;
  },

  // 延長確定（時間制）: plan_duration_minutes / plan_ends_at を延長し、延長オーダーも登録する
  // minutes 単位で送信（15〜720）。メニュー名「延長N分」パターンと一致必須。
  extendTime: async (visitId: number, storeId: number, data: { menu_item_id: number; minutes: number }) => {
    const url = API_ENDPOINTS.stores.visitsExtendTime!(storeId, visitId);
    const response = await axios.post<ApiResponse>(url, data);
    return response.data;
  },

  // キャスト割り当て
  assignCast: async (visitId: number, castId: number, storeId?: number) => {
    // 店舗スコープがある場合は stores/{storeId}/visits/... を優先（/visits/... はdeprecated）
    if (storeId && API_ENDPOINTS.stores.visitsAssignCast) {
      const url = API_ENDPOINTS.stores.visitsAssignCast(storeId, visitId);
      const response = await axios.post<ApiResponse>(url, { cast_id: castId });
      return response.data;
    }
    const response = await axios.post<ApiResponse>(API_ENDPOINTS.visits.assignCast(visitId), { cast_id: castId });
    return response.data;
  },

  // キャスト割り当て解除
  unassignCast: async (visitId: number, storeId?: number) => {
    // 店舗スコープがある場合は stores/{storeId}/visits/... を優先（/visits/... はdeprecated）
    if (storeId && API_ENDPOINTS.stores.visitsUnassignCast) {
      const url = API_ENDPOINTS.stores.visitsUnassignCast(storeId, visitId);
      const response = await axios.delete<ApiResponse>(url);
      return response.data;
    }
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.visits.unassignCast(visitId));
    return response.data;
  },

  // 指定キャストだけを visit_casts から削除（個別解除）
  removeCasts: async (visitId: number, castIds: number[], storeId: number) => {
    const url = API_ENDPOINTS.stores.visitsRemoveCasts(storeId, visitId);
    const response = await axios.post<ApiResponse>(url, { cast_ids: castIds });
    return response.data;
  },

  // 顧客タイプ (free/called) を手動トグル。
  // - called 切替時は primary_cast_id 必須 (担当未設定の呼び客を作らない)。
  // - free 切替時は primary_cast_id を渡さない (BE で null クリア + active visit_casts 自動退席)。
  // 認可 (BE 側で実施):
  // - called: STAFF/ADMIN または関連伝票 CAST
  // - free: STAFF/ADMIN のみ
  setCustomerType: async (
    visitId: number,
    payload:
      | { customer_type: 'called'; primary_cast_id: number }
      | { customer_type: 'free' },
    storeId: number,
  ) => {
    const url = API_ENDPOINTS.stores.visitsSetCustomerType!(storeId, visitId);
    const response = await axios.patch<ApiResponse>(url, payload);
    return response.data;
  },

  // 未精算visitsを取得（scope: before_open=営業開始前に残っている未精算, in_window=指定営業日の未精算）
  getUnclosedVisits: async (params: { store_id?: number; business_date: string; scope?: 'before_open' | 'in_window' }) => {
    const storeId = params.store_id;
    if (storeId) {
      const response = await axios.get<ApiResponse>(
        API_ENDPOINTS.stores.visitsUnclosed(storeId),
        { params: { business_date: params.business_date, scope: params.scope } }
      );
      return response.data;
    }
    // 互換（旧ルート）。Laravel側で 410 の可能性があるため、基本は store_id を渡すこと。
    const response = await axios.get<ApiResponse>(API_ENDPOINTS.visits.unclosed, { params });
    return response.data;
  },

  // 伝票ボード用スナップショット（Redisキャッシュ前提）
  boardSnapshot: async (params: { store_id: number; business_date: string }) => {
    const url = API_ENDPOINTS.stores.visitsBoardSnapshot!(params.store_id);
    const response = await axios.get<ApiResponse>(url, { params: { business_date: params.business_date } });
    return response.data;
  },

  // 席追加（visit_tables）
  attachTables: async (storeId: number, visitId: number, tableNos: string[]) => {
    const url = API_ENDPOINTS.stores.visitsTablesAttach!(storeId, visitId);
    const response = await axios.post<ApiResponse>(url, { table_nos: tableNos });
    // success: false の場合はエラーとして扱う（念のため）
    if (!response.data.success) {
      throw new Error(response.data.message || '席追加に失敗しました');
    }
    return response.data;
  },

  // 席解放（visit_tables の sub を解除）
  detachTables: async (storeId: number, visitId: number, tableNos: string[]) => {
    const url = API_ENDPOINTS.stores.visitsTablesDetach!(storeId, visitId);
    const response = await axios.post<ApiResponse>(url, { table_nos: tableNos });
    // success: false の場合はエラーとして扱う（念のため）
    if (!response.data.success) {
      throw new Error(response.data.message || '席解放に失敗しました');
    }
    return response.data;
  },

  // 席移動（visit の 1 席を別の空き席へ付け替え。main/sub 両対応）
  moveTable: async (storeId: number, visitId: number, fromTableNo: string, toTableNo: string) => {
    const url = API_ENDPOINTS.stores.visitsTablesMove!(storeId, visitId);
    const response = await axios.post<ApiResponse>(url, { from_table_no: fromTableNo, to_table_no: toTableNo });
    if (!response.data.success) {
      throw new Error(response.data.message || '席移動に失敗しました');
    }
    return response.data;
  },

  // 訂正作成（新方式：visit_corrections テーブル）
  createCorrection: async (storeId: number, visitId: number, data: {
    type: 'add' | 'refund';
    reason: string;
    items: Array<{
      source_order_item_id?: number; // 返金時
      menu_item_id?: number; // 追加時
      cast_id?: number; // キャスト必須メニュー時
      qty: number;
    }>;
  }) => {
    const response = await axios.post<{
      success: boolean;
      data?: {
        id: number;
        parent_visit_id: number;
        type: 'add' | 'refund';
        reason: string;
        total_amount: number;
        items: Array<{
          id: number;
          source_order_item_id: number | null;
          menu_item_id: number | null;
          cast_id: number | null;
          qty: number;
          unit_price: number;
          amount: number;
        }>;
        created_at: string;
      };
      error_code?: string;
      message?: string;
    }>(`/api/stores/${storeId}/visits/${visitId}/corrections`, data);
    return response.data;
  },

  // 支払方法の訂正（会計済みの伝票・管理者のみ。現金・電子マネー・その他の間だけ。UX 監査 C-2）
  correctPaymentMethod: async (storeId: number, visitId: number, data: {
    payment_method: 'cash' | 'electronic_money' | 'other';
    reason: string;
  }) => {
    const response = await axios.patch<{
      success: boolean;
      message?: string;
      error_code?: string;
      data?: { visit_id: number; payment_method: string; previous_payment_method: string };
    }>(`/api/stores/${storeId}/visits/${visitId}/payment-method`, data);
    return response.data;
  },

  // 訂正一覧取得（新方式：visit_corrections テーブル）
  listCorrections: async (storeId: number, visitId: number) => {
    const response = await axios.get<{
      success: boolean;
      data: {
        parent_visit_id: number;
        business_date?: string | null;
        original_total: number;
        corrections_total: number;
        display_total: number;
        // お客様の支払いの基準（値引き・サービス料・税・カード手数料込み。2026-10-03・UX 監査 C-3）。
        // customer_basis が null の伝票（会計の元で合計を再現できない古い伝票）は税・サ抜きの値しか出せない
        customer_display_total?: number | null;
        pricing_model?: string | null;
        customer_basis?: {
          subtotal_amount: number; discount_amount: number; discount_percent: number; service_rate: number;
          tax_rate: number; pricing_model: string; card_surcharge_rate: number; total_amount: number;
        } | null;
        order_items: Array<{
          id: number;
          menu_item_id: number;
          name: string;
          qty: number;
          unit_price: number;
          line_amount: number;
          refunded_qty: number;
          remaining_qty: number;
        }>;
        corrections: Array<{
          id: number;
          type: 'add' | 'refund';
          reason: string;
          created_at: string;
          created_by: {
            employee_id: number;
            name: string;
          } | null;
          items: Array<{
            id: number;
            source_order_item_id: number | null;
            menu_item_id: number | null;
            cast_id: number | null;
            cast_name: string | null;
            name: string;
            qty: number;
            unit_price: number;
            amount: number;
          }>;
          total_amount: number;
          customer_amount?: number | null;
        }>;
        corrections_count: number;
        // この伝票の月の月次給与が、誰の分まで計算・支払い済みか（2026-10-02・UX 監査 E-18。無ければ null）
        // 名前は見ている人が給与画面で読める給与の分だけ。読めない分は *_others が true
        payroll_notice?: { month: string; paid: string[]; calculated: string[]; paid_others?: boolean; calculated_others?: boolean } | null;
      };
    }>(`/api/stores/${storeId}/visits/${visitId}/corrections`);
    return response.data;
  },
};

/**
 * 締め前チェックレスポンスの型定義
 */
export interface ClosingCheckResponse {
  success: boolean;
  data: {
    business_date: string;
    business_day_id: number | null;
    status: 'preparing' | 'opened' | 'closed' | 'locked';
    cutoff_time: string;
    uncheckout_visits_count: number;
    uncheckout_visits: Array<{
      id: number;
      table_nos: string[];
      customer_name: string;
      party_size: number;
      status: string;
      checked_in_at: string;
      business_date: string | null;
    }>;
    unclocked_employees_count: number;
    unclocked_employees: Array<{
      attendance_id: number;
      user_id: number;
      employee_id: number;
      employee_name: string;
      role_type: 'STAFF' | 'CAST';
      working_role: string;
      clock_in_time: string;
      ymd: string;
    }>;
  };
}

/**
 * 会計処理API
 */
export const accountingAPI = {
  // 会計処理（チェックアウト）
  checkout: async (data: {
    visit_id: number;
    discount_amount?: number;
    discount_reason?: string;
    payment_method: 'cash' | 'credit_card' | 'electronic_money' | 'other';
    payment_amount: number;
    change_amount?: number;
    note?: string;
  }) => {
    const response = await axios.post<ApiResponse<Payment>>(API_ENDPOINTS.accounting.checkout, data);
    return response.data;
  },

  // レシート発行
  generateReceipt: async (data: {
    visit_id: number;
    receipt_type: 'receipt' | 'invoice';
  }) => {
    const response = await axios.post<ApiResponse<Receipt>>(API_ENDPOINTS.accounting.receipts, data);
    return response.data;
  },

  // 売上レポート取得
  getSalesReport: async (params: {
    date_from: string;
    date_to: string;
    report_type: 'daily' | 'monthly' | 'payment_method' | 'cast' | 'menu' | 'hourly';
    report_basis?: 'paid_at' | 'business_date';
  }) => {
    const response = await axios.get<ApiResponse<SalesReport[]>>(API_ENDPOINTS.accounting.salesReport, { params });
    return response.data;
  },

  // 締め前チェック（未精算伝票・未退勤者の一覧取得）
  getClosingCheck: async (params?: { business_date?: string }) => {
    const response = await axios.get<ClosingCheckResponse>('/api/accounting/closing-check', { params });
    return response.data;
  },
};

/**
 * データエクスポートAPI (CSV ダウンロード、ADMIN 限定)
 *
 * サーバー生成 CSV を blob で受けてダウンロードする (downloadCsv 参照)。
 * sales は店舗スコープ (X-Store-ID = 選択中店舗が interceptor で自動付与)。
 */
export const exportsAPI = {
  downloadSales: (params: { date_from: string; date_to: string; basis?: 'paid_at' | 'business_date' }) =>
    downloadCsv(API_ENDPOINTS.exports.sales, params, `sales_${params.date_from}_${params.date_to}.csv`),

  downloadPayrolls: (params: { date_from: string; date_to: string; store_id?: number; period_type?: 'daily' | 'monthly' }) =>
    downloadCsv(API_ENDPOINTS.exports.payrolls, params, `payrolls_${params.date_from}_${params.date_to}.csv`),

  downloadAttendances: (params: { date_from: string; date_to: string; store_id?: number }) =>
    downloadCsv(API_ENDPOINTS.exports.attendances, params, `attendances_${params.date_from}_${params.date_to}.csv`),

  downloadBacks: (params: { date_from: string; date_to: string; store_id?: number; cast_id?: number }) =>
    downloadCsv(API_ENDPOINTS.exports.backs, params, `backs_${params.date_from}_${params.date_to}.csv`),
};

/**
 * 通知設定API
 */
export const notificationSettingAPI = {
  getSettings: async () => {
    // available_methods: 認証状態(メール設定済み/LINE連携済み)で選択できる通知方法
    const response = await axios.get<ApiResponse<NotificationSetting> & { available_methods?: Record<string, boolean> }>(
      API_ENDPOINTS.notificationSettings.index
    );
    return response.data;
  },
  updateSettings: async (data: NotificationSettingUpdateRequest) => {
    const response = await axios.put<ApiResponse<NotificationSetting> & { available_methods?: Record<string, boolean> }>(
      API_ENDPOINTS.notificationSettings.index, data
    );
    return response.data;
  },
  getNotificationMethods: async () => {
    const response = await axios.get<ApiResponse<NotificationMethod>>(API_ENDPOINTS.notificationSettings.methods);
    return response.data;
  },
};

// シフトタイプAPI
export const shiftTypeAPI = {
  getShiftTypes: async () => {
    const response = await axios.get<ApiResponse<ShiftType[]>>(API_ENDPOINTS.shiftTypes.index);
    return response.data;
  },
  createShiftType: async (data: ShiftTypeCreateRequest) => {
    const response = await axios.post<ApiResponse<ShiftType>>(API_ENDPOINTS.shiftTypes.store, data);
    return response.data;
  },
  updateShiftType: async (id: number, data: ShiftTypeUpdateRequest) => {
    const response = await axios.put<ApiResponse<ShiftType>>(API_ENDPOINTS.shiftTypes.update.replace(':id', id.toString()), data);
    return response.data;
  },
  deleteShiftType: async (id: number) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.shiftTypes.destroy.replace(':id', id.toString()));
    return response.data;
  },
};

// 週単位シフト申請API
export const weeklyShiftRequestAPI = {
  getWeeklyShiftRequests: async (weekStart?: string, storeId?: number, year?: number, month?: number) => {
    const params: any = {};
    if (weekStart) params.week_start = weekStart;
    if (storeId) params.store_id = storeId;
    if (year) params.year = year;
    if (month) params.month = month;
    const response = await axios.get<ApiResponse<WeeklyShiftRequest[]>>(API_ENDPOINTS.weeklyShiftRequests.index, { params });
    return response.data;
  },
  createWeeklyShiftRequest: async (data: WeeklyShiftRequestCreateRequest, storeId?: number) => {
    const payload = storeId ? { ...data, store_id: storeId } : data;
    const response = await axios.post<ApiResponse<WeeklyShiftRequest>>(API_ENDPOINTS.weeklyShiftRequests.store, payload);
    return response.data;
  },
  getWeeklyShiftRequest: async (id: number) => {
    const response = await axios.get<ApiResponse<WeeklyShiftRequest>>(API_ENDPOINTS.weeklyShiftRequests.show.replace(':id', id.toString()));
    return response.data;
  },
  updateWeeklyShiftRequest: async (id: number, data: WeeklyShiftRequestUpdateRequest) => {
    const response = await axios.put<ApiResponse<WeeklyShiftRequest>>(API_ENDPOINTS.weeklyShiftRequests.update.replace(':id', id.toString()), data);
    return response.data;
  },
  deleteWeeklyShiftRequest: async (id: number) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.weeklyShiftRequests.destroy.replace(':id', id.toString()));
    return response.data;
  },
  submitWeeklyShiftRequest: async (id: number, storeId?: number) => {
    const url = API_ENDPOINTS.weeklyShiftRequests.submit.replace(':id', id.toString());
    const response = await axios.post<ApiResponse<WeeklyShiftRequest>>(url, storeId ? { store_id: storeId } : undefined, storeId ? undefined : undefined);
    return response.data;
  },
  cancelWeeklyShiftRequest: async (id: number, storeId?: number) => {
    const url = API_ENDPOINTS.weeklyShiftRequests.submit.replace(':id', id.toString()).replace('/submit','/cancel');
    const response = await axios.post<ApiResponse<WeeklyShiftRequest>>(url, storeId ? { store_id: storeId } : undefined);
    return response.data;
  },
  // 承認待ち一覧（既定は会社スコープ）
  // storeScoped=true を渡すと、X-Store-ID で確定した店舗の週だけに絞る。
  // シフト調整画面へ「リスト表示」として埋め込むとき用（隣のカレンダーと母集団を揃えるため）。
  getPending: async (storeId?: number, opts?: { includeApproved?: boolean; includeRejected?: boolean; includeAll?: boolean; storeScoped?: boolean }) => {
    const params: any = {};
    if (storeId) params.store_id = storeId;
    if (opts?.storeScoped) params.store_scoped = 1;
    if (opts?.includeApproved) params.include_approved = 1;
    if (opts?.includeRejected) params.include_rejected = 1;
    if (opts?.includeAll) params.include_all = 1;
    const response = await axios.get<ApiResponse<WeeklyShiftRequest[]>>(API_ENDPOINTS.weeklyShiftRequests.index + '/pending', { params });
    return response.data;
  },
  // storeScoped=true を渡すと、X-Store-ID の店舗に属さない週は 404 になる。
  // 店舗に絞って表示している側（シフト調整の「リスト表示」）から使うこと。
  // 会社スコープで全店を並べる単独ページからは渡さない（従来どおり全店を操作できる）。
  approve: async (id: number, storeId?: number, opts?: { storeScoped?: boolean }) => {
    const url = API_ENDPOINTS.weeklyShiftRequests.submit.replace(':id', id.toString()).replace('/submit','/approve');
    const body = { ...(storeId ? { store_id: storeId } : {}), ...(opts?.storeScoped ? { store_scoped: 1 } : {}) };
    const response = await axios.post<ApiResponse<WeeklyShiftRequest>>(url, Object.keys(body).length ? body : undefined);
    return response.data;
  },
  reject: async (id: number, reason?: string, storeId?: number, opts?: { storeScoped?: boolean }) => {
    const url = API_ENDPOINTS.weeklyShiftRequests.submit.replace(':id', id.toString()).replace('/submit','/reject');
    const response = await axios.post<ApiResponse<WeeklyShiftRequest>>(url, {
      reason,
      ...(storeId ? { store_id: storeId } : {}),
      ...(opts?.storeScoped ? { store_scoped: 1 } : {}),
    });
    return response.data;
  },
  reopen: async (id: number, storeId?: number, opts?: { storeScoped?: boolean }) => {
    const url = API_ENDPOINTS.weeklyShiftRequests.submit.replace(':id', id.toString()).replace('/submit','/reopen');
    const body = { ...(storeId ? { store_id: storeId } : {}), ...(opts?.storeScoped ? { store_scoped: 1 } : {}) };
    const response = await axios.post<ApiResponse<WeeklyShiftRequest>>(url, Object.keys(body).length ? body : undefined);
    return response.data;
  },
  // v1.1: 週内全Decision取得（キャスト履歴用）
  getDecisions: async (id: number, storeId?: number) => {
    const url = API_ENDPOINTS.weeklyShiftRequests.decisions(id);
    const params = storeId ? { store_id: storeId } : undefined;
    const response = await axios.get<ApiResponse<{
      weekly_request: {
        id: number;
        employee_id: number;
        store_id: number | null;
        store: { id: number; name: string } | null;
        week_start: string;
        week_end: string;
        status: string;
      };
      decisions: Array<{
        date: string;
        decision: {
          id: number;
          status: 'pending' | 'approved' | 'rejected' | 'on_hold';
          status_label: string;
          reason: string | null;
          decided_at: string | null;
          decided_by_name: string | null;
        } | null;
        daily_shift: {
          id: number;
          store_id: number | null;
          start_time: string | null;
          end_time: string | null;
          shift_template: { id: number; name: string } | null;
        };
      }>;
    }>>(url, { params });
    return response.data;
  },
};

// シフトテンプレートAPI
export const shiftTemplateAPI = {
  getShiftTemplates: async (params?: { shift_type?: string; is_active?: boolean; sort_by?: string; sort_order?: string; store_id?: number; scope?: string }) => {
    const response = await axios.get<ApiResponse<ShiftTemplate[]>>(API_ENDPOINTS.shiftTemplates.index, { params });
    return response.data;
  },
  createShiftTemplate: async (data: ShiftTemplateCreateRequest) => {
    const response = await axios.post<ApiResponse<ShiftTemplate>>(API_ENDPOINTS.shiftTemplates.store, data);
    return response.data;
  },
  getShiftTemplate: async (id: number) => {
    const response = await axios.get<ApiResponse<ShiftTemplate>>(API_ENDPOINTS.shiftTemplates.show.replace(':id', id.toString()));
    return response.data;
  },
  updateShiftTemplate: async (id: number, data: ShiftTemplateUpdateRequest) => {
    const response = await axios.put<ApiResponse<ShiftTemplate>>(API_ENDPOINTS.shiftTemplates.update.replace(':id', id.toString()), data);
    return response.data;
  },
  deleteShiftTemplate: async (id: number) => {
    const response = await axios.delete<ApiResponse>(API_ENDPOINTS.shiftTemplates.destroy.replace(':id', id.toString()));
    return response.data;
  },
  getShiftTypeExamples: async () => {
    const response = await axios.get<ApiResponse<Record<string, string>>>(API_ENDPOINTS.shiftTemplates.shiftTypeExamples);
    return response.data;
  },
  getActiveTemplates: async () => {
    const response = await axios.get<ApiResponse<ShiftTemplate[]>>(API_ENDPOINTS.shiftTemplates.active);
    return response.data;
  },
};

/**
 * シフトプランナー公開API
 */
export const shiftPlannerAPI = {
  getMonthData: async (params: { store_id: number; year: number; month: number }) => {
    const { store_id, ...query } = params;
    const response = await axios.get<ApiResponse<{
      year: number;
      month: number;
      monthStart: string;
      monthEnd: string;
      casts: Array<{ id: number; name: string }>;
      entries: Array<{
        id: number;
        castId: number;
        castName: string;
        date: string;
        startTime: string;
        endTime: string;
        status: string;
        isPublished: boolean;
      }>;
      unassigned: Array<{
        id: number;
        castId: number;
        castName: string;
        date: string;
        startTime: string;
        endTime: string;
        status: string;
      }>;
      capacities: Array<{ date: string; target: number; count: number }>;
    }>>(`/api/stores/${store_id}/shift-planner`, { params: query });
    return response.data;
  },
  publish: async (payload: { year: number; month: number; store_id: number }) => {
    const { store_id, ...body } = payload;
    const response = await axios.post<ApiResponse>(`/api/stores/${store_id}/shift-planner/publish`, body);
    return response.data;
  },
  unpublish: async (payload: { year: number; month: number; store_id: number }) => {
    const { store_id, ...body } = payload;
    const response = await axios.post<ApiResponse>(`/api/stores/${store_id}/shift-planner/unpublish`, body);
    return response.data;
  },
  saveEntries: async (payload: {
    store_id: number;
    year: number;
    month: number;
    // id は daily_shifts.id（画面上の合成IDではない）。unassign=true で未割当プールへ戻す（単日で非公開化）
    entries: Array<{ id: number; date?: string; start_time?: string; end_time?: string; unassign?: boolean }>;
  }) => {
    const { store_id, ...body } = payload;
    const response = await axios.post<ApiResponse<{ saved: number[] }>>(`/api/stores/${store_id}/shift-planner/entries`, body);
    return response.data;
  },
};


// (旧手動転記 deductionAPI は給与計算 (PayrollCalculationJob::buildDeductionItems) へ統合済)

/**
 * 任意控除マスターAPI
 */
export const deductionsMasterAPI = {
  getAll: async () => {
    const response = await axios.get<{ success: boolean; data: DeductionMaster[] }>(API_ENDPOINTS.deductionsMaster!.index);
    return response.data;
  },
  create: async (payload: DeductionMasterCreateRequest) => {
    const response = await axios.post<{ success: boolean; data: DeductionMaster }>(API_ENDPOINTS.deductionsMaster!.store, payload);
    return response.data;
  },
  update: async (id: number, payload: DeductionMasterUpdateRequest) => {
    const response = await axios.put<{ success: boolean; data: DeductionMaster }>(API_ENDPOINTS.deductionsMaster!.update(id), payload);
    return response.data;
  },
  destroy: async (id: number) => {
    const response = await axios.delete<{ success: boolean }>(API_ENDPOINTS.deductionsMaster!.destroy(id));
    return response.data;
  },
  /** 表示順の一括更新。渡した id の順に採番される */
  reorder: async (ids: number[]) => {
    const response = await axios.put<{ success: boolean; data: DeductionMaster[] }>(
      '/api/deductions-master/reorder',
      { ids },
    );
    return response.data;
  },
};

/**
 * 日別シフト判断（Decision）API v1.1
 */
export const dailyShiftDecisionAPI = {
  // 一覧取得（管理用）
  index: async (params?: {
    from_date?: string;
    to_date?: string;
    status?: 'pending' | 'approved' | 'rejected' | 'on_hold';
    employee_id?: number;
    store_id?: number;
  }) => {
    const response = await axios.get<ApiResponse<Array<{
      id: number;
      daily_shift_id: number;
      status: 'pending' | 'approved' | 'rejected' | 'on_hold';
      status_label: string;
      reason: string | null;
      decided_by: number | null;
      decided_at: string | null;
      daily_shift: {
        id: number;
        date: string;
        start_time: string | null;
        end_time: string | null;
        employee: { id: number; stage_name: string } | null;
        shift_template: { id: number; name: string } | null;
        weekly_shift_request: {
          id: number;
          week_start: string;
          week_end: string;
          status: string;
        } | null;
      } | null;
    }>> & { meta: { total: number; pending_count: number; approved_count: number; rejected_count: number; on_hold_count: number } }>(
      API_ENDPOINTS.dailyShiftDecisions.index,
      { params }
    );
    return response.data;
  },

  // 単体取得
  show: async (id: number, storeId?: number) => {
    const params = storeId ? { store_id: storeId } : undefined;
    const response = await axios.get<ApiResponse<any>>(
      API_ENDPOINTS.dailyShiftDecisions.show(id),
      { params }
    );
    return response.data;
  },

  // 更新（承認/却下/保留）
  update: async (id: number, data: {
    status: 'pending' | 'approved' | 'rejected' | 'on_hold';
    reason?: string;
  }, storeId?: number) => {
    const payload = storeId ? { ...data, store_id: storeId } : data;
    const response = await axios.put<ApiResponse<{
      id: number;
      daily_shift_id: number;
      status: string;
      reason: string | null;
      decided_by: number | null;
      decided_at: string | null;
    }>>(API_ENDPOINTS.dailyShiftDecisions.update(id), payload);
    return response.data;
  },

  // 一括更新
  bulk: async (decisions: Array<{
    id: number;
    status: 'pending' | 'approved' | 'rejected' | 'on_hold';
    reason?: string;
  }>, storeId?: number) => {
    const payload = storeId ? { decisions, store_id: storeId } : { decisions };
    const response = await axios.post<ApiResponse<{
      updated_count: number;
      decisions: Array<{ id: number; status: string; reason: string | null }>;
    }>>(API_ENDPOINTS.dailyShiftDecisions.bulk, payload);
    return response.data;
  },
};

/**
 * 操作ログAPI
 */
export const operationLogAPI = {
  // 操作ログ一覧取得
  index: async (params?: {
    company_id?: number;
    store_id?: number;
    business_date?: string;
    category?: 'admin' | 'business' | 'payroll';
    type?: string;
    limit?: number;
  }) => {
    const response = await axios.get<OperationLogListResponse>(API_ENDPOINTS.operationLogs.index, { params });
    return response.data;
  },

  // 操作ログ作成
  create: async (data: OperationLogCreateRequest) => {
    const response = await axios.post<ApiResponse<OperationLog>>(API_ENDPOINTS.operationLogs.store, data);
    return response.data;
  },

  // アーカイブ済みログ取得
  getArchived: async (params?: {
    company_id?: number;
    store_id?: number;
    from?: string;
    to?: string;
  }) => {
    const response = await axios.get<OperationLogListResponse>(API_ENDPOINTS.operationLogs.archived, { params });
    return response.data;
  },

  // ログ検索
  search: async (params?: {
    company_id?: number;
    store_id?: number;
    query?: string;
    category?: 'admin' | 'business' | 'payroll';
    type?: string;
    from?: string;
    to?: string;
    limit?: number;
  }) => {
    const response = await axios.get<OperationLogListResponse>(API_ENDPOINTS.operationLogs.search, { params });
    return response.data;
  },

  // 異常検知ログ取得
  getAnomalies: async (params?: {
    company_id?: number;
    store_id?: number;
    from?: string;
    to?: string;
  }) => {
    const response = await axios.get<OperationLogListResponse>(API_ENDPOINTS.operationLogs.anomalies, { params });
    return response.data;
  },
};

/**
 * 給与計算ランAPI（v1）
 */
export const payrollRunAPI = {
  // ラン一覧
  getRuns: async (params?: { period_type?: 'daily' | 'monthly'; store_id?: number; status?: string; payroll_month?: string }) => {
    const response = await axios.get<ApiResponse>('/api/payroll-runs', { params });
    return response.data;
  },

  // 対象キャスト候補取得（日払い計算対象選択用）
  getTargetCasts: async (params: { store_id: number; target_date: string }) => {
    const response = await axios.get<ApiResponse>('/api/payroll-runs/target-casts', { params });
    return response.data;
  },

  // ラン作成（計算ジョブ投入）
  createRun: async (data: {
    period_type: 'daily' | 'monthly';
    store_id: number;
    payroll_month?: string;
    target_date?: string;
    cast_ids?: number[];
    note?: string;
  }) => {
    const response = await axios.post<ApiResponse>('/api/payroll-runs', data);
    return response.data;
  },

  // ラン詳細（ポーリング用）
  getRun: async (id: number) => {
    const response = await axios.get<ApiResponse>(`/api/payroll-runs/${id}`);
    return response.data;
  },

  // ラン配下の給与一覧
  getRunPayrolls: async (id: number) => {
    const response = await axios.get<ApiResponse>(`/api/payroll-runs/${id}/payrolls`);
    return response.data;
  },

  // 再計算
  recalculate: async (id: number) => {
    const response = await axios.post<ApiResponse>(`/api/payroll-runs/${id}/recalculate`);
    return response.data;
  },

  // 給与確定
  closePayroll: async (payrollId: number) => {
    const response = await axios.post<ApiResponse>(`/api/payrolls/${payrollId}/close`);
    return response.data;
  },

  // 給与の確定を取り消す（確定済み・未払い → 未確定。UX 監査 X-1 / A-1）
  reopenPayroll: async (payrollId: number) => {
    const response = await axios.post<ApiResponse>(`/api/payrolls/${payrollId}/reopen`);
    return response.data;
  },

  // 給与支払い
  payPayroll: async (payrollId: number, data: { payment_method: 'cash' | 'bank_transfer' | 'other' }) => {
    const response = await axios.post<ApiResponse>(`/api/payrolls/${payrollId}/pay`, data);
    return response.data;
  },

  // 一括確定
  // 状態が違う・操作できない店舗の給与は数えずに 200 が返るので、件数は closed_count を見る
  bulkClose: async (payrollIds: number[]) => {
    const response = await axios.post<ApiResponse & { closed_count?: number }>('/api/payrolls/bulk-close', { payroll_ids: payrollIds });
    return response.data;
  },

  // 一括支払い（件数は paid_count を見る。理由は一括確定と同じ）。
  // skipped は払えなかった給与と理由（置き換えられた計算・同じ日をほかの計算で支払い済み。2026-09-26）
  bulkPay: async (payrollIds: number[], paymentMethod: 'cash' | 'bank_transfer' | 'other') => {
    const response = await axios.post<ApiResponse & { paid_count?: number; skipped?: { id: number; reason: string }[] }>('/api/payrolls/bulk-pay', {
      payroll_ids: payrollIds,
      payment_method: paymentMethod,
    });
    return response.data;
  },

  // 給与明細詳細（payroll_items 含む）
  getPayrollDetail: async (payrollId: number) => {
    const response = await axios.get<ApiResponse>(`/api/payrolls/${payrollId}`);
    return response.data;
  },
};

/**
 * セット料金ルール管理API
 */
export interface SetPricingRule {
  id: number;
  company_id: number;
  store_id: number | null;
  // 2026-07-26 ボトル制解禁: bottle ルールを許可。bottle は duration_minutes=null (= 時間無制限)。
  plan_type: 'time' | 'bottle' | 'all_you_can_drink';
  arrival_time_from: string | null;
  arrival_time_to: string | null;
  duration_minutes: number | null;
  base_charge_per_person: number;
  active: boolean;
  sort_order: number;
  // 2026-06-17 Phase 2/3 + 2026-06-18 Phase 5A: 来店構成条件 + 人数範囲 + 表示ラベル (memory: project_female_set_pricing_design)
  // NULL=ワイルドカード (= 構成問わずマッチ)、 値域は 'general' / 'female_only' / 'mixed_with_female' (Phase 5A で追加)
  party_composition: 'general' | 'female_only' | 'mixed_with_female' | null;
  party_size_min: number | null;
  party_size_max: number | null;
  rule_label: string | null;
  created_at: string;
  updated_at: string;
}

export interface SetPricingRuleRequest {
  store_id?: number | null;
  plan_type: 'time' | 'bottle' | 'all_you_can_drink';
  arrival_time_from?: string | null;
  arrival_time_to?: string | null;
  duration_minutes: number | null;
  base_charge_per_person: number;
  active?: boolean;
  sort_order?: number;
  party_composition?: 'general' | 'female_only' | 'mixed_with_female' | null;
  party_size_min?: number | null;
  party_size_max?: number | null;
  rule_label?: string | null;
}

export const setPricingRuleAPI = {
  getAll: async (storeId: number, options?: { activeOnly?: boolean }) => {
    const params = options?.activeOnly ? { active_only: 1 } : undefined;
    const response = await axios.get<ApiResponse<SetPricingRule[]>>(
      API_ENDPOINTS.stores.setPricingRulesIndex!(storeId),
      { params }
    );
    return response.data;
  },

  create: async (storeId: number, data: SetPricingRuleRequest) => {
    const response = await axios.post<ApiResponse<SetPricingRule>>(
      API_ENDPOINTS.stores.setPricingRulesStore!(storeId),
      data
    );
    return response.data;
  },

  update: async (storeId: number, ruleId: number, data: Partial<SetPricingRuleRequest>) => {
    const response = await axios.put<ApiResponse<SetPricingRule>>(
      API_ENDPOINTS.stores.setPricingRulesUpdate!(storeId, ruleId),
      data
    );
    return response.data;
  },

  destroy: async (storeId: number, ruleId: number) => {
    const response = await axios.delete<ApiResponse>(
      API_ENDPOINTS.stores.setPricingRulesDestroy!(storeId, ruleId)
    );
    return response.data;
  },
};

export type RoundingUnit   = 1 | 10 | 15 | 30 | 60;
export type RoundingType   = 'floor' | 'ceil' | 'round';
export type RoundingScope  = 'per_shift' | 'per_month';
export type RoundingMethod = 'total_time' | 'time_clock';

export interface PayrollRoundingSettings {
  wage_rounding_unit_minutes: RoundingUnit;
  wage_rounding_type: RoundingType;
  wage_rounding_scope: RoundingScope;
  wage_rounding_method: RoundingMethod;
  base_hourly_wage?: number | null;
}

export interface StorePayrollRoundingSettings {
  store_override: {
    wage_rounding_unit_minutes: RoundingUnit | null;
    wage_rounding_type: RoundingType | null;
    wage_rounding_scope: RoundingScope | null;
    wage_rounding_method: RoundingMethod | null;
    base_hourly_wage_override?: number | null;
  };
  effective: PayrollRoundingSettings;
  company?: {
    base_hourly_wage?: number | null;
  };
}

export const payrollSettingAPI = {
  getCompany: async () => {
    const res = await axios.get<{ success: boolean; data: PayrollRoundingSettings }>('/api/payroll-settings');
    return res.data.data;
  },
  updateCompany: async (data: PayrollRoundingSettings) => {
    const res = await axios.put<{ success: boolean; data: PayrollRoundingSettings }>('/api/payroll-settings', data);
    return res.data.data;
  },
  getStore: async (storeId: number) => {
    const res = await axios.get<{ success: boolean; data: StorePayrollRoundingSettings }>(`/api/payroll-settings/store/${storeId}`);
    return res.data.data;
  },
  updateStore: async (storeId: number, data: { wage_rounding_unit_minutes: RoundingUnit | null; wage_rounding_type: RoundingType | null; wage_rounding_scope: RoundingScope | null; wage_rounding_method: RoundingMethod | null }) => {
    const res = await axios.put<{ success: boolean; data: any }>(`/api/payroll-settings/store/${storeId}`, data);
    return res.data.data;
  },
};

// ================================================================
// 日払い給与API
// ================================================================
export const dailyPayrollAPI = {
  // 依頼作成（キャスト用）
  createRequest: async (attendanceId: number, note?: string) => {
    const res = await axios.post<{ success: boolean; data: any }>('/api/daily-payroll-requests', {
      attendance_id: attendanceId,
      note,
    });
    return res.data;
  },
  // 自分の依頼確認（キャスト用）
  // business_date: 単日指定 (既存)
  // from/to: 営業日範囲指定 (履歴セクション用、 2026-05-23 追加)
  // limit: 取得件数 (デフォルト 10、 max 100)
  // store_id: 店舗絞り込み (2026-06-26 追加。 複数店舗所属キャストの他店舗依頼混入防止)
  // status: CSV 受付 (例: "requested,processing")。 BE 側で whereIn する (2026-06-26 追加)
  getMyRequests: async (
    params:
      | {
          business_date?: string;
          from?: string;
          to?: string;
          limit?: number;
          store_id?: number;
          status?: string;
        }
      | string = {},
  ) => {
    // 後方互換: 文字列で渡された場合は business_date として扱う (旧シグネチャ)
    const queryParams = typeof params === 'string' ? { business_date: params } : params;
    const res = await axios.get<{ success: boolean; data: any[] }>('/api/daily-payroll-requests/my', { params: queryParams });
    return res.data;
  },
  // 依頼一覧（スタッフ/管理者用）
  // statuses (複数) は単一 status より優先。バナーで requested+processing を同時表示するのに使う。
  // string (CSV) と string[] の両方を許容 (backend が両方ハンドルする)。
  getRequests: async (params: { store_id?: number; status?: string; statuses?: string | string[]; business_date?: string; page?: number } = {}) => {
    const res = await axios.get<{ success: boolean; data: any[]; meta: any }>('/api/daily-payroll-requests', { params });
    return res.data;
  },
  // 伝票（卓）ごとのバック明細（配属店舗のスタッフ＝黒服も・本人キャスト。バックだけ。2026-09-26）
  backDetail: async (requestId: number) => {
    const res = await axios.get<{ success: boolean; data: any }>('/api/daily-payroll/back-detail', { params: { request_id: requestId } });
    return res.data;
  },
  // 依頼キャンセル
  cancelRequest: async (id: number) => {
    const res = await axios.patch<{ success: boolean; data: any }>(`/api/daily-payroll-requests/${id}/cancel`);
    return res.data;
  },
  // 処理開始（管理者用）
  startProcessing: async (id: number) => {
    const res = await axios.patch<{ success: boolean; data: any }>(`/api/daily-payroll-requests/${id}/start-processing`);
    return res.data;
  },
  // 今夜の未処理の依頼の件数・合計・お札の内訳（管理者用。UX 監査 D-7）。選択中の店舗（X-Store-ID は interceptor が付ける）
  getPendingSummary: async () => {
    const res = await axios.get<{ success: boolean; data: { count: number; counted: number; unavailable: number; total: number; notes: { '10000': number; '5000': number; '1000': number }; coins_total: number; items: Array<{ request_id: number; cast_id: number; amount: number }> } }>(
      '/api/daily-payroll/pending-summary',
    );
    return res.data;
  },
  // 今日の手取り見込み（キャスト本人用。依頼する前に見せる。UX 監査 D-4）。X-Store-ID は選択中の店舗が interceptor で付く
  getMyEstimate: async (businessDate: string) => {
    const res = await axios.get<{ success: boolean; data: { ready: boolean; business_date: string; total?: { base_salary: number; back_total: number; allowance_total: number; deduction_total: number; total_salary: number } } }>(
      '/api/daily-payroll/my-estimate',
      { params: { business_date: businessDate } },
    );
    return res.data;
  },
  // プレビュー（管理者用）
  preview: async (requestId: number) => {
    const res = await axios.get<{ success: boolean; data: any }>('/api/daily-payroll/preview', {
      params: { request_id: requestId },
    });
    return res.data;
  },
  // 確定+支払い（管理者用）
  settle: async (data: {
    request_id: number;
    idempotency_key: string;
    adjustments?: Array<{ label: string; amount: number }>;
    payment_method: 'cash' | 'bank_transfer' | 'other';
  }) => {
    const res = await axios.post<{ success: boolean; data: any }>('/api/daily-payroll/settle', data);
    return res.data;
  },
};

// ===== Phase 2: 延長リクエスト API =====

export type ExtensionRequestStatus = 'pending' | 'approved' | 'rejected' | 'expired';

export interface ExtensionRequestResource {
  id: number;
  visit_id: number;
  requested_by_employee_id: number;
  status: ExtensionRequestStatus;
  approved_by_employee_id: number | null;
  approved_menu_item_id: number | null;
  approved_minutes: number | null;
  approved_party_size: number | null;
  approved_at: string | null;
  rejected_reason: string | null;
  expires_at: string | null;
  created_at: string | null;
}

export const extensionRequestAPI = {
  /** キャスト: 延長希望を送信 */
  create: async (storeId: number, visitId: number) => {
    const url = API_ENDPOINTS.stores.extensionRequestsStore!(storeId, visitId);
    const res = await axios.post<ApiResponse<ExtensionRequestResource>>(url, {});
    return res.data;
  },

  /** スタッフ: 承認待ち一覧取得（関連 visit 情報込み） */
  listPending: async (storeId: number) => {
    const url = API_ENDPOINTS.stores.extensionRequestsIndex!(storeId);
    const res = await axios.get<ApiResponse<any[]>>(url);
    return res.data;
  },

  /** スタッフ: ポーリング用の軽量 COUNT */
  pendingCount: async (storeId: number) => {
    const url = API_ENDPOINTS.stores.extensionRequestsPendingCount!(storeId);
    const res = await axios.get<ApiResponse<{ pending_count: number }>>(url);
    return res.data;
  },

  /** スタッフ: 承認（プランと延長分数を指定） */
  approve: async (
    storeId: number,
    requestId: number,
    data: { menu_item_id: number; minutes: number; party_size?: number | null },
  ) => {
    const url = API_ENDPOINTS.stores.extensionRequestsApprove!(storeId, requestId);
    const res = await axios.post<ApiResponse<ExtensionRequestResource>>(url, data);
    return res.data;
  },

  /** スタッフ: 却下（理由は任意） */
  reject: async (storeId: number, requestId: number, reason?: string) => {
    const url = API_ENDPOINTS.stores.extensionRequestsReject!(storeId, requestId);
    const res = await axios.post<ApiResponse<ExtensionRequestResource>>(url, { reason });
    return res.data;
  },
};



// ============================================================
// キャスト手当（キャッチ手当等の店負担バック）
//
// 売上（order_lines）とは独立したレコード。伝票から付与しても会計・売上・
// お客様控えレシートには影響せず、給与の加算項目としてのみ効く。
// memory: project_store_funded_back_gap
// ============================================================

export interface CastAllowanceTypeRow {
  id: number;
  allowance_cd: string | null;
  name: string;
  default_amount: number;
  active: boolean;
}

export interface VisitAllowanceRow {
  id: number;
  cast_id: number;
  cast_name: string;
  allowance_type_id: number;
  allowance_name: string | null;
  qty: number;
  unit_amount: number;
  amount: number;
  note: string | null;
}

export const allowanceAPI = {
  /** 手当マスタ一覧（会社スコープ・ADMIN） */
  listTypes: async (includeInactive = false) => {
    const res = await axios.get<ApiResponse<CastAllowanceTypeRow[]>>('/api/allowance-types', {
      params: includeInactive ? { include_inactive: 1 } : undefined,
    });
    return res.data;
  },

  createType: async (data: { name: string; default_amount: number; active?: boolean }) => {
    const res = await axios.post<ApiResponse<CastAllowanceTypeRow>>('/api/allowance-types', data);
    return res.data;
  },

  updateType: async (id: number, data: Partial<{ name: string; default_amount: number; active: boolean }>) => {
    const res = await axios.put<ApiResponse<CastAllowanceTypeRow>>(`/api/allowance-types/${id}`, data);
    return res.data;
  },

  /** 無効化（物理削除しない。過去の実績が参照しているため） */
  deactivateType: async (id: number) => {
    const res = await axios.delete<ApiResponse>(`/api/allowance-types/${id}`);
    return res.data;
  },

  /** 店舗別の実効単価一覧 */
  listStoreSettings: async (storeId: number) => {
    const res = await axios.get<ApiResponse<Array<{
      allowance_type_id: number;
      name: string;
      default_amount: number;
      override_amount: number | null;
      override_active: boolean | null;
      effective_amount: number;
    }>>>(`/api/stores/${storeId}/allowance-settings`);
    return res.data;
  },

  upsertStoreSetting: async (
    storeId: number,
    allowanceTypeId: number,
    data: { override_amount: number | null; active?: boolean },
  ) => {
    const res = await axios.put<ApiResponse>(
      `/api/stores/${storeId}/allowance-settings/${allowanceTypeId}`,
      data,
    );
    return res.data;
  },

  /** 表示順の一括更新。渡した id の順に採番される */
  reorder: async (ids: number[]) => {
    const res = await axios.put<ApiResponse<CastAllowanceTypeRow[]>>('/api/allowance-types/reorder', { ids });
    return res.data;
  },

  clearStoreSetting: async (storeId: number, allowanceTypeId: number) => {
    const res = await axios.delete<ApiResponse>(`/api/stores/${storeId}/allowance-settings/${allowanceTypeId}`);
    return res.data;
  },

  /** 手当の件数変更（同一種別は1行に集約されるため、行を増やさず件数を編集する） */
  updateQty: async (allowanceId: number, qty: number) => {
    const res = await axios.put<ApiResponse<VisitAllowanceRow>>(`/api/allowances/${allowanceId}`, { qty });
    return res.data;
  },

  /** 手当の取消（給与確定後は 409） */
  cancel: async (allowanceId: number) => {
    const res = await axios.delete<ApiResponse>(`/api/allowances/${allowanceId}`);
    return res.data;
  },

  /**
   * 入力用の手当種別一覧（店舗別上書きを解決した実効単価つき）。
   * 設定画面用の listStoreSettings は ADMIN 限定なので、入力側はこちらを使う。
   */
  listTypesForInput: async (storeId: number) => {
    const res = await axios.get<ApiResponse<Array<{
      allowance_type_id: number;
      name: string;
      effective_amount: number;
    }>>>(`/api/stores/${storeId}/allowance-types`);
    return res.data;
  },

  /**
   * キャスト×営業日 の手当一覧（伝票紐づきの有無を問わず全件）。
   * 日払い処理画面で「今この人に何が付いているか」を出すために使う。
   */
  listForCastDate: async (storeId: number, castId: number, businessDate: string) => {
    const res = await axios.get<ApiResponse<VisitAllowanceRow[]> & { total?: number }>(
      `/api/stores/${storeId}/allowances`,
      { params: { cast_id: castId, business_date: businessDate } },
    );
    return res.data;
  },

  /**
   * 伝票に紐づけずに手当を付与（付け忘れの後追い付与）。
   * 営業中に付け忘れて会計してしまった分を日払い処理の場で救済するための経路。
   */
  grantWithoutVisit: async (
    storeId: number,
    data: { cast_id: number; allowance_type_id: number; business_date: string; qty?: number; note?: string },
  ) => {
    const res = await axios.post<ApiResponse<VisitAllowanceRow>>(`/api/stores/${storeId}/allowances`, data);
    return res.data;
  },
};

/** キャスト控除の 1 行（cast_deductions）。手当の VisitAllowanceRow と対称。 */
export interface CastDeductionRow {
  id: number;
  deduction_id: number;
  deduction_name: string | null;
  system_key: string | null;
  qty: number;
  unit_amount: number;
  amount: number;
  note?: string | null;
}

/** 入力用の控除種別（店舗別上書きを解決した実効単価つき） */
export interface DeductionTypeOption {
  deduction_id: number;
  name: string;
  system_key: string | null;
  effective_amount: number;
  /** キャスト本人が自分に記録できる控除か（false なら画面は表示するが非活性にする） */
  can_self_record: boolean;
}

/**
 * キャスト控除（行モデル）。allowanceAPI の鏡像。
 *
 * ⚠️ 旧 dailyServiceAPI（cast_daily_services の固定 4 列）は 2026-08-25 に廃止した。
 *    書き込みは 410 を返す。
 */
export const castDeductionAPI = {
  /** 入力用の控除種別一覧。本人にも返るが can_self_record で操作可否を分ける */
  listTypesForInput: async (storeId: number) => {
    const res = await axios.get<ApiResponse<DeductionTypeOption[]>>(
      `/api/stores/${storeId}/cast-deduction-types`,
    );
    return res.data;
  },

  /**
   * 店舗×営業日 の控除サマリ（キャストごとの件数と合計）。
   * 「今日のキャスト」一覧のバッジ用。純粋スタッフ / ADMIN のみ。
   */
  getSummaryByStoreAndDate: async (storeId: number, businessDate: string) => {
    const res = await axios.get<ApiResponse<Array<{ cast_id: number; qty: number; amount: number }>>>(
      `/api/stores/${storeId}/cast-deductions/summary`,
      { params: { business_date: businessDate } },
    );
    return res.data;
  },

  /** キャスト×営業日 の控除一覧 */
  listForCastDate: async (storeId: number, castId: number, businessDate: string) => {
    const res = await axios.get<ApiResponse<CastDeductionRow[]> & { total?: number }>(
      `/api/stores/${storeId}/cast-deductions`,
      { params: { cast_id: castId, business_date: businessDate } },
    );
    return res.data;
  },

  /** 控除を記録（同一キャスト×営業日×種別は件数が加算される） */
  record: async (
    storeId: number,
    data: { cast_id: number; deduction_id: number; business_date: string; qty?: number; note?: string },
  ) => {
    const res = await axios.post<ApiResponse<CastDeductionRow>>(`/api/stores/${storeId}/cast-deductions`, data);
    return res.data;
  },

  /** 件数変更（単価は記録時のスナップショットを維持する） */
  updateQty: async (deductionId: number, qty: number) => {
    const res = await axios.put<ApiResponse<CastDeductionRow>>(`/api/cast-deductions/${deductionId}`, { qty });
    return res.data;
  },

  /** 控除の取消（給与確定後は 409） */
  cancel: async (deductionId: number) => {
    const res = await axios.delete<ApiResponse>(`/api/cast-deductions/${deductionId}`);
    return res.data;
  },
};
